import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Tooltip,
  TextField, Tabs, Tab, Autocomplete, InputAdornment, Popover,
  Checkbox
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TaskAlt as TaskAltIcon,
  Cancel as CancelIcon,
  Folder as FolderIcon,
  FileDownload as DownloadIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  FactCheck as FactCheckIcon,
  HourglassTop as HourglassTopIcon,
  HistoryEdu as HistoryEduIcon,
  AccountTree as AccountTreeIcon,
  ExpandMore as ExpandMoreIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import config from '../../config/config';
import CollapsibleTable from '../../components/table';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import CancelacionModal from '../../components/CancelacionModal';
import FileActionModal from '../../components/FileActionModal';
import SnackbarAlert from '../../components/SnackbarAlert';
import { useSession } from '../../components/hooks/useSession';
import { getTablaPagosAut, obtenerArchivosPago, cancelarPago } from '../../services/pagosAutService';
import { buildFilterPayload } from '../../utils/filterUtils';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';

const apiUrl = config.apiBaseUrl;
const CHECK_MARK = '\u2713';

const parseMoneyVal = (r) => {
  if (!r) return 0;
  const raw = r.MONTOSOLICITADO ?? r.MONTO_SOLICITADO ?? r.pago ?? r.MONTO ?? r.TOTALOC ?? r.TOTAL ?? 0;
  if (typeof raw === 'number') return isNaN(raw) ? 0 : raw;
  const parsed = Number(String(raw).replace(/[^0-9.-]/g, ''));
  return isNaN(parsed) ? 0 : parsed;
};

const calcDiasRetraso = (fechaStr) => {
  if (!fechaStr) return 0;
  const text = String(fechaStr).trim();
  let date;
  const slashMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) {
    date = new Date(Number(slashMatch[3]), Number(slashMatch[2]) - 1, Number(slashMatch[1]));
  } else {
    date = new Date(text.split(/[T\s]/)[0]);
  }
  if (!date || isNaN(date.getTime())) return 0;
  date.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - date.getTime()) / 86400000));
};

const applyThinBorder = (cell, color = 'D9E2F3') => {
  cell.border = {
    top: { style: 'thin', color: { argb: color } },
    left: { style: 'thin', color: { argb: color } },
    bottom: { style: 'thin', color: { argb: color } },
    right: { style: 'thin', color: { argb: color } },
  };
};

const buildHistorialItem = (row) => ({
  autsolpagoid: row.ID || row.AUTSOLPAGOID || null,
  empresaId: row.empresaId || row.EMPRESAID || row.IDEMPRESA || null,
  empresa: row.empresa || row.EMPRESA || '',
  folioOc: row.orden || row.FOLIO_OC || row.FOLIO || '',
  proveedor: row.proveedor || row.PROVEEDOR || '',
  monto: row.pago ?? row.MONTO_SOLICITADO ?? row.MONTOSOLICITADO ?? 0,
  divisa: row.moneda || row.DIVISA || row.MONEDA || '',
  centroCosto: row.centroCosto || row.CENTROCOSTO || row.CENTRO_COSTO || row.NUMERO_CENTRO_COSTO || '',
  departamento: row.departamento || row.DEPARTAMENTO || row.NOMBRE_DEPARTAMENTO || '',
  fechaSolicitud: row.FECHASOLICITUD || row.FECHA_SOLICITUD || row.FECHA || '',
});

const getReporteKeys = (row) => {
  const keys = [];
  const autsolpagoid = row.AUTSOLPAGOID || row.ID_SOLICITUD || (row.ID && !isNaN(Number(row.ID)) ? row.ID : '');
  if (autsolpagoid) keys.push(`AUT:${autsolpagoid}`);
  const empresaId = row.EMPRESAID || row.IDEMPRESA || row.empresaId || '';
  const folioOc = row.FOLIO_OC || row.orden || row.FOLIO || '';
  if (empresaId && folioOc) keys.push(`${empresaId}|${folioOc}`);
  return keys;
};

const fmt = (val) => {
  const n = Number(val);
  if (isNaN(n)) return val || '$0.00';
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
};

const cleanExhibit = (val) => {
  if (!val) return '';
  return String(val).replace(/^Solicitud\s*/i, '').trim();
};

const fmtDate = (d) => {
  if (!d || d === 'N/A') return '';
  const str = String(d).trim();
  const rawDate = str.includes('T') ? str.split('T')[0] : str.split(' ')[0];
  const match = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return rawDate || '';
};

const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];
  if (Array.isArray(idEmpresa)) return idEmpresa.map(x => Number(x)).filter(n => Number.isFinite(n) && n > 0);
  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map(x => Number(x)).filter(n => Number.isFinite(n) && n > 0);
      } catch {}
    }
    if (s.includes(',')) {
      return s.split(',').map(x => Number(x.trim())).filter(n => Number.isFinite(n) && n > 0);
    }
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

const getFirstDayOfYear = () => {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
};

const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getSelectedSummary = (values = []) => {
  if (!values.length) return '';
  if (values.length === 1) {
    const first = values[0];
    return typeof first === 'string' ? first : first?.nombre || String(first);
  }
  return `${values.length} sel.`;
};

const renderSummaryInput = (params, label, selectedValues = []) => {
  const summary = getSelectedSummary(selectedValues);
  const isSelected = Boolean(summary);

  return (
    <TextField
      {...params}
      placeholder={summary ? undefined : label}
      InputProps={{
        ...params.InputProps,
        startAdornment: summary ? (
          <Box
            component="span"
            sx={{
              maxWidth: 'calc(100% - 24px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.68rem)',
              fontWeight: 700,
              color: '#0655A5',
            }}
          >
            {summary}
          </Box>
        ) : params.InputProps.startAdornment,
      }}
      sx={{
        ...(isSelected ? {
          '& .MuiInputBase-root': {
            bgcolor: '#f0f7ff !important',
            borderColor: '#93c5fd !important',
            '& fieldset': {
              borderColor: '#60a5fa !important',
              borderWidth: '1.5px !important',
            }
          }
        } : {})
      }}
    />
  );
};

const chkIcon = <CheckBoxOutlineBlankIcon fontSize="small" sx={{ fontSize: 15, color: '#94a3b8' }} />;
const chkCheckedIcon = <CheckBoxIcon fontSize="small" sx={{ fontSize: 15, color: '#0655A5' }} />;

const renderCheckboxOption = (props, option, { selected }) => (
  <li
    {...props}
    style={{
      padding: '4px 8px',
      fontSize: '0.72rem',
      fontWeight: selected ? 700 : 500,
      backgroundColor: selected ? '#e0f2fe' : 'transparent',
      color: selected ? '#0369a1' : '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      borderBottom: '1px solid #f8fafc',
    }}
  >
    <Checkbox
      icon={chkIcon}
      checkedIcon={chkCheckedIcon}
      style={{ marginRight: 4, padding: 0 }}
      checked={selected}
      size="small"
    />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
      {typeof option === 'string' ? option : option?.nombre || ''}
    </Box>
  </li>
);

const compactAutocompleteSx = {
  '& .MuiInputBase-root': {
    height: '28px !important',
    minHeight: '28px !important',
    maxHeight: '28px !important',
    borderRadius: '10px !important',
    bgcolor: '#ffffff',
    fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem)',
    fontWeight: 500,
    px: '8px !important',
    py: '0px !important',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    '& fieldset': { 
      borderColor: '#cbd5e1 !important',
      top: 0,
      '& legend': { display: 'none' }
    },
    '&:hover fieldset': { borderColor: '#94a3b8 !important' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5 !important', borderWidth: '1.5px !important' },
    '& .MuiAutocomplete-input': {
      minWidth: '20px !important',
      py: '0px !important',
      fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem)',
      fontWeight: 500,
      color: '#0f172a',
      '&::placeholder': {
        color: '#64748b',
        opacity: 1,
        fontWeight: 500,
      }
    },
    '& .MuiAutocomplete-endAdornment': {
      position: 'absolute',
      right: '6px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: 0,
      '& .MuiAutocomplete-clearIndicator': {
        display: 'none',
      },
      '& .MuiIconButton-root': {
        p: '2px',
        color: '#64748b',
        '&:hover': { color: '#0655A5', bgcolor: 'transparent' },
        '& .MuiSvgIcon-root': { fontSize: '15px' }
      }
    }
  }
};

export default function ResumenCentroCosto() {
  const { loggedInUsername, usuarioId, idPerfil, nombreEmpresa, idEmpresa, nombrePerfil } = useSession();

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [viewMode, setViewMode] = useState('FULL'); 
  const [activeFullTab, setActiveFullTab] = useState(0); 

  // Estado Acordeón
  const [openSplitSec1, setOpenSplitSec1] = useState(false);
  const [openSplitSec2, setOpenSplitSec2] = useState(false);
  const [openSplitSec3, setOpenSplitSec3] = useState(false);

  // SECCIÓN 1: Datos PagosAut
  const [dataPagosAut, setDataPagosAut] = useState([]);
  const [selectedRowsAut, setSelectedRowsAut] = useState([]);

  // SECCIÓN 2: Datos ReporteAutorizadas
  const [dataAutorizadas, setDataAutorizadas] = useState([]);
  const [selectedRowsReporte, setSelectedRowsReporte] = useState([]);
  const [historialMap, setHistorialMap] = useState({});
  const historialMapRef = useRef({});
  const activeFullTabRef = useRef(activeFullTab);
  const initialFetchDoneRef = useRef(false);

  // SECCIÓN 3: Datos SolicitudesProgramadas
  const [dataProgramadas, setDataProgramadas] = useState([]);
  const [loadingProgramadas, setLoadingProgramadas] = useState(false);
  const programadasLoadedRef = useRef(false);

  // Filtros Locales (Por defecto: desde inicio de año hasta el día de hoy)
  const [filtroEmpresas, setFiltroEmpresas] = useState([]);
  const [filtroProyectos, setFiltroProyectos] = useState([]);
  const [filtroDivisiones, setFiltroDivisiones] = useState([]);
  const [filtroCc, setFiltroCc] = useState('');
  const [filtroDepto, setFiltroDepto] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [filtroOrden, setFiltroOrden] = useState('');
  const [fechaInicio, setFechaInicio] = useState(getFirstDayOfYear());
  const [fechaFin, setFechaFin] = useState(getTodayDate());
  const [hierarchicalAnchorEl, setHierarchicalAnchorEl] = useState(null);

  // Catálogos jerárquicos
  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [proyectosCatalogo, setProyectosCatalogo] = useState([]);
  const [divisionesCatalogo, setDivisionesCatalogo] = useState([]);
  const [centrosCostoCatalogo, setCentrosCostoCatalogo] = useState([]);

  // Modales y Alertas
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [observacionesCancel, setObservacionesCancel] = useState('');
  const [cancelTargetRow, setCancelTargetRow] = useState(null);

  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [initialFileId, setInitialFileId] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const empresaIds = useMemo(() => {
    const sessionIds = normalizeEmpresaArray(idEmpresa);
    if (sessionIds.length > 0) return sessionIds;
    const localId = Number(localStorage.getItem('idEmpresa') || 0);
    return localId > 0 ? [localId] : [];
  }, [idEmpresa]);

  // ── Fetch Sección 1: PagosAut ─────────────────────────────────────────────
  const fetchPagosAutData = useCallback(async () => {
    if (!apiUrl || !loggedInUsername || !empresaIds.length) return [];
    try {
      const respData = await getTablaPagosAut({
        filterData: { SESSION: loggedInUsername, page: 1, pageSize: 300 },
        nombreEmpresa,
        idEmpresa: empresaIds,
        nombrePerfil
      });

      const rawRows = respData?.contenido || respData?.rows || (Array.isArray(respData) ? respData : []);
      return rawRows.map(r => ({
        ...r,
        ID: r.ID || r.ID_SOLICITUD || r.AUTSOLPAGOID || r.FOLIO,
        MONTO_UNIFICADO: parseMoneyVal(r),
        FECHA_SOLICITUD_FORMATO: fmtDate(r.FECHASOLICITUD || r.FECHA_OC || r.FECHA),
        FECHA_AUTORIZACION_UNIFICADA: fmtDate(r.FECHA_AUTORIZACION || r.FECHA_AUT) || '🔴 Pendiente',
        FECHA_REPORTE_PAGO: fmtDate(r.FECHA_SOLICITUD_PAGO || r.FECHA_PAGO),
        DIAS_SIN_PAGO: `${calcDiasRetraso(r.FECHASOLICITUD || r.FECHA)}d`,
        FECHA_COMPROBANTE: fmtDate(r.FECHA_COMPROBANTE),
        EXHIBIT: cleanExhibit(r.EXHIBICION || r.exhibicion || r.EXHIBIT || ''),
        ESTATUS_COMPACTO: r.ESTATUS || 'Por Autorizar',
      }));
    } catch (err) {
      console.error('Error cargando PagosAut:', err);
      return [];
    }
  }, [loggedInUsername, empresaIds, nombreEmpresa, nombrePerfil]);

  // ── Fetch Sección 2: ReporteAutorizadas (ESTATUS === 3) ───────────────────
  const fetchReporteAutorizadasData = useCallback(async () => {
    if (!apiUrl || !loggedInUsername || !empresaIds.length || !idPerfil) return [];
    try {
      const response = await axios.post(`${apiUrl}/reporteAutorizadasAdmin`, {
        idEmpresa: empresaIds,
        usuarioId,
        filterData: {},
      });

      const rawRows = response.data?.contenido || [];
      const uniqueRows = new Map();

      rawRows.forEach((row) => {
        const estatusId = Number(row.ESTATUSID ?? row.ID_ESTATUS ?? row.ESTATUS_ID ?? row.estatusId ?? 3);
        if (estatusId !== 3) return;

        // Si ya tiene exhibit, se considera ya pagada/tramitada y no debe aparecer en Pendientes por Pagar
        const exhibitVal = cleanExhibit(row.EXHIBICION || row.exhibicion || row.EXHIBIT || '');
        if (exhibitVal) return;

        const key = row.AUTSOLPAGOID || `${row.EMPRESAID || row.IDEMPRESA || ''}|${row.FOLIO_OC || row.FOLIO || ''}|${row.ID || ''}`;
        if (key && !uniqueRows.has(key)) {
          // Normalizar: el nombre del CC viene en CENTROCOSTO, el número en NUMEROCC
          const ccNombre = String(row.CENTROCOSTO || row.CENTRO_COSTO || '').trim();
          const ccNumero = String(row.NUMEROCC || row.NUMERO_CC || row.NUMERO_CENTRO_COSTO || '').trim();
          uniqueRows.set(key, {
            ...row,
            ID: row.ID || row.AUTSOLPAGOID || row.FOLIO_OC || row.FOLIO,
            // Asegurar que CENTROCOSTO = nombre y NUMEROCC = número para PagosAutCentroCell
            CENTROCOSTO: ccNombre || ccNumero,
            NUMEROCC: ccNumero,
            NOMBRE_CC: ccNombre,
            orden: row.FOLIO_OC || row.FOLIO || row.ORDEN || '',
            empresa: row.EMPRESA || row.NOMBRE_EMPRESA || '',
            pago: parseMoneyVal(row),
            moneda: (row.DIVISA || row.MONEDA || 'MXN').toString().toUpperCase(),
            concepto: row.CONCEPTOPAGO || row.CONCEPTO || row.OBSERVACIONES || '',
            descripcion: Array.isArray(row.partidas) && row.partidas.length
              ? row.partidas.map(p => p.DESCRIPCION_PART || p.DESCRIPCION || '').filter(Boolean).join('\n')
              : (row.DESCRIPCION || row.DESCRIPCION_OC || row.DESCRIPCION_PART || row.CONCEPTOPAGO || row.CONCEPTO || row.OBSERVACIONES || ''),
            proveedor: row.PROVEEDOR || 'SIN PROVEEDOR',
            centroCosto: ccNombre || ccNumero,
            departamento: row.DEPARTAMENTO || '',
            NUMERO_ECONOMICO: row.NUMERO_ECONOMICO || row.NUM_ECONOMICO || row.ECONOMICO || '',
            ID_PROYECTO: Number(row.ID_PROYECTO || row.IDPROYECTO || 0),
            PROYECTO: row.PROYECTO || row.NOMBRE_PROYECTO || '',
            ID_DIVISION: Number(row.ID_DIVISION || row.IDDIVISION || 0),
            DIVISION: row.DIVISION || row.NOMBRE_DIVISION || '',
            MONTO_UNIFICADO: parseMoneyVal(row),
            FECHA_SOLICITUD_FORMATO: fmtDate(row.FECHASOLICITUD || row.FECHA_OC || row.FECHA),
            FECHA_AUTORIZACION_UNIFICADA: fmtDate(row.FECHA_AUTORIZACION || row.FECHA_AUT || row.FECHA),
            FECHA_REPORTE_PAGO: fmtDate(row.FECHA_REPORTE_PAGO || row.FECHA_SOLICITUD_PAGO || row.FECHA_REPORTE),
            DIAS_SIN_PAGO: `${calcDiasRetraso(row.FECHA_REPORTE_PAGO || row.FECHA_SOLICITUD_PAGO || row.FECHA_REPORTE || row.FECHASOLICITUD)}d`,
            FECHA_COMPROBANTE: fmtDate(row.FECHA_COMPROBANTE),
            EXHIBIT: cleanExhibit(row.EXHIBICION || row.exhibicion || row.EXHIBIT || ''),
            ESTATUS_COMPACTO: row.ESTATUS || 'Autorizada',
            VECES_REPORTADO: row.VECES_REPORTADO || 0,
          });
        }
      });

      const list = Array.from(uniqueRows.values());

      // Llamada a historial asíncrona no bloqueante en segundo plano
      if (list.length > 0) {
        axios.post(`${apiUrl}/reporteAutorizadas/historial`, {
          items: list.map(buildHistorialItem)
        }).then(histRes => {
          if (histRes.data?.historial) {
            historialMapRef.current = histRes.data.historial;
            setHistorialMap(histRes.data.historial);
          }
        }).catch(e => console.error('Error historial no bloqueante:', e));
      }

      return list;
    } catch (err) {
      console.error('Error cargando ReporteAutorizadas:', err);
      return [];
    }
  }, [loggedInUsername, empresaIds, usuarioId, idPerfil]);

  // ── Fetch Sección 3: SolicitudesProgramadas ──────────────────────────────
  const fetchSolicitudesProgramadasData = useCallback(async () => {
    if (!apiUrl || !loggedInUsername || !empresaIds.length || !idPerfil) return [];
    try {
      const serverFilterData = {
        FECHA_INICIO: fechaInicio || '',
        FECHA_FIN: fechaFin || '',
        FOLIO: filtroOrden || '',
        PROVEEDOR: filtroProveedor || '',
        CENTRO_COSTO: filtroCc ? [filtroCc] : '',
        DEPARTAMENTO: filtroDepto ? [filtroDepto] : '',
        DIVISA: '',
        ESTATUS: '',
      };

      const combinedFilter = {
        ...buildFilterPayload(serverFilterData),
        globalQuery: '',
        sortField: 'FECHASOLICITUD',
        sortDir: 'DESC',
        page: 1,
        pageSize: 150,
      };

      const response = await axios.post(`${apiUrl}/solicitudesProgramadasTabla1`, {
        idEmpresa: empresaIds,
        idPerfil,
        nombreEmpresa,
        loggedInUsername,
        filterData: combinedFilter,
      });

      const rawRows = response.data?.contenido || response.data?.rows || (Array.isArray(response.data) ? response.data : []);
      return rawRows.map(r => ({
        ...r,
        ID: r.ID || r.FOLIO || r.AUTSOLPAGOID,
        MONTO_UNIFICADO: parseMoneyVal(r),
        FECHA_SOLICITUD_FORMATO: fmtDate(r.FECHASOLICITUD || r.FECHA_OC || r.FECHA),
        FECHA_AUTORIZACION_UNIFICADA: fmtDate(r.FECHA_AUTORIZACION || r.FECHA_AUT) || 'N/A',
        FECHA_COMPROBANTE: fmtDate(r.FECHA_COMPROBANTE),
        EXHIBIT: cleanExhibit(r.EXHIBICION || r.exhibicion || r.EXHIBIT || ''),
        ESTATUS_COMPACTO: r.ESTATUS || 'Proceso',
      }));
    } catch (err) {
      console.error('Error cargando SolicitudesProgramadas:', err);
      return [];
    }
  }, [apiUrl, loggedInUsername, empresaIds, idPerfil, nombreEmpresa, filtroCc, filtroDepto, fechaInicio, fechaFin, filtroOrden, filtroProveedor]);

  // ── Helpers de Fechas y Procesamiento ────────────────────────────────────
  const resolveFechaReportePago = useCallback((row) => {
    if (row.FECHA_REPORTE_PAGO) return { formatted: fmtDate(row.FECHA_REPORTE_PAGO), rawDate: row.FECHA_REPORTE_PAGO };
    const keys = getReporteKeys(row);
    const map = historialMapRef.current || {};
    for (const k of keys) {
      const hist = map[k];
      const fechaReporteLote = hist?.eventos?.[hist.eventos.length - 1]?.fecha;
      if (fechaReporteLote) return { formatted: fmtDate(fechaReporteLote), rawDate: fechaReporteLote };
    }
    if (row.FECHA_SOLICITUD_PAGO && row.FECHA_SOLICITUD_PAGO !== '🟡 Sin solicitar') return { formatted: fmtDate(row.FECHA_SOLICITUD_PAGO), rawDate: row.FECHA_SOLICITUD_PAGO };
    if (row.FECHA_REPORTE) return { formatted: fmtDate(row.FECHA_REPORTE), rawDate: row.FECHA_REPORTE };
    return { formatted: '', rawDate: null };
  }, []);

  const resolveDiasSinPago = useCallback((row, fechaReporteObj) => {
    const estatusId = Number(row.ESTATUSID ?? row.ID_ESTATUS ?? row.ESTATUS_ID ?? row.estatusId ?? 0);
    const estatusText = String(row.ESTATUS || row.ESTATUS_COMPACTO || '').toLowerCase();

    const esPagadaOCancelada = estatusId === 10 || estatusText.includes('pagad') || estatusText.includes('cancelad');
    if (esPagadaOCancelada) return '';

    if (!fechaReporteObj.rawDate || !fechaReporteObj.formatted) return '';

    const dias = calcDiasRetraso(fechaReporteObj.rawDate);
    return `${dias}d`;
  }, []);

  const processRows = useCallback((rows) => (rows || []).map(r => {
    const fReporte = resolveFechaReportePago(r);
    return {
      ...r,
      FECHA_AUTORIZACION_UNIFICADA: (r.FECHA_AUTORIZACION && r.FECHA_AUTORIZACION !== 'N/A') ? fmtDate(r.FECHA_AUTORIZACION) : (fmtDate(r.FECHASOLICITUD || r.FECHA) || ''),
      FECHA_REPORTE_PAGO: fReporte.formatted,
      DIAS_SIN_PAGO: resolveDiasSinPago(r, fReporte),
      FECHA_COMPROBANTE: fmtDate(r.FECHA_COMPROBANTE || r.uploaded_at || r.FECHA_ALTA || r.FECHA_PAGO || ''),
      VECES_REPORTADO: r.VECES_REPORTADO || 0,
    };
  }), [resolveFechaReportePago, resolveDiasSinPago]);

  const applyBatchDates = useCallback((rows, fHistMap, fCmpMap) => {
    return (rows || []).map(row => {
      const autId = row.AUTSOLPAGOID || row.ID_SOLICITUD || row.ID || row.FOLIO;
      const fechaHist = fHistMap[autId];
      const estatusId = Number(row.ESTATUSID ?? row.ID_ESTATUS ?? row.ESTATUS_ID ?? row.estatusId ?? 0);
      const estatusText = String(row.ESTATUS || row.ESTATUS_COMPACTO || '').toLowerCase();
      let fechaAut = row.FECHA_AUTORIZACION_UNIFICADA;
      if (estatusId === 1 || estatusText.includes('default') || estatusText.includes('por autorizar') || estatusText.includes('pendiente')) {
        fechaAut = '';
      } else if (fechaHist) {
        fechaAut = fmtDate(fechaHist);
      } else if (row.FECHA_AUTORIZACION && row.FECHA_AUTORIZACION !== 'N/A') {
        fechaAut = fmtDate(row.FECHA_AUTORIZACION);
      } else if (row.FECHA_AUT && row.FECHA_AUT !== 'N/A') {
        fechaAut = fmtDate(row.FECHA_AUT);
      }

      const idKeys = [row.AUTSOLPAGOID, row.ID_SOLICITUD, row.ID, row.FOLIO].filter(Boolean);
      let fechaBatch = null;
      for (const k of idKeys) {
        if (fCmpMap[k]) { fechaBatch = fCmpMap[k]; break; }
      }
      let fechaCmp = row.FECHA_COMPROBANTE;
      if (fechaBatch) fechaCmp = fmtDate(fechaBatch);
      else if (row.FECHA_COMPROBANTE && row.FECHA_COMPROBANTE !== 'N/A') fechaCmp = fmtDate(row.FECHA_COMPROBANTE);
      else if (row.uploaded_at) fechaCmp = fmtDate(row.uploaded_at);
      else if (row.FECHA_ALTA) fechaCmp = fmtDate(row.FECHA_ALTA);
      else if (row.FECHA_PAGO) fechaCmp = fmtDate(row.FECHA_PAGO);

      return {
        ...row,
        FECHA_AUTORIZACION_UNIFICADA: fechaAut,
        FECHA_COMPROBANTE: fechaCmp,
      };
    });
  }, []);

  // ── Carga On-Demand de la Sección 3 (Vista General) ───────────────────────
  const loadProgramadasData = useCallback(async (force = false) => {
    if (programadasLoadedRef.current && !force) return;
    setLoadingProgramadas(true);
    try {
      const progAut = await fetchSolicitudesProgramadasData();
      const processed = processRows(progAut);
      setDataProgramadas(processed);
      programadasLoadedRef.current = true;

      const progAutIds = [...new Set(
        progAut.map(r => {
          const val = r.AUTSOLPAGOID || r.ID_SOLICITUD || r.ID || r.FOLIO;
          const num = Number(val);
          return Number.isInteger(num) && num > 0 ? num : null;
        }).filter(Boolean)
      )];

      if (progAutIds.length) {
        axios.post(`${apiUrl}/fechasAutorizacionBatch`, { autIds: progAutIds }).then(resBatch => {
          const fHistMap = resBatch.data?.fechasMap || {};
          const fCmpMap = resBatch.data?.fechasComprobanteMap || {};
          setDataProgramadas(prev => applyBatchDates(prev, fHistMap, fCmpMap));
        }).catch(errHist => console.error('Error fechas batch programadas:', errHist));
      }
    } catch (err) {
      console.error('Error cargando solicitudes programadas:', err);
    } finally {
      setLoadingProgramadas(false);
    }
  }, [fetchSolicitudesProgramadasData, processRows, apiUrl, applyBatchDates]);

  const loadProgramadasDataRef = useRef(null);
  loadProgramadasDataRef.current = loadProgramadasData;

  // ── Fetch General Optimizado Progresivo (Pestaña activa primero) ───
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setSelectedRowsAut([]);
    setSelectedRowsReporte([]);
    try {
      // 1. Cargar primero la sección 2 (Pendientes por Pagar) y la sección 1 para que aparezca al instante
      const [rAut, pAut] = await Promise.all([
        fetchReporteAutorizadasData(),
        fetchPagosAutData()
      ]);

      setDataAutorizadas(processRows(rAut));
      setDataPagosAut(processRows(pAut));
      setLoading(false); // Liberamos la pantalla inmediatamente (~400ms)

      // 2. Cargar fechas históricas para Secciones 1 y 2 inmediatamente en segundo plano (~50ms)
      const allRows = [...pAut, ...rAut];
      const allAutIds = [...new Set(
        allRows.map(r => {
          const val = r.AUTSOLPAGOID || r.ID_SOLICITUD || r.ID || r.FOLIO;
          const num = Number(val);
          return Number.isInteger(num) && num > 0 ? num : null;
        }).filter(Boolean)
      )];

      if (allAutIds.length) {
        axios.post(`${apiUrl}/fechasAutorizacionBatch`, { autIds: allAutIds }).then(resBatch => {
          const fHistMap = resBatch.data?.fechasMap || {};
          const fCmpMap = resBatch.data?.fechasComprobanteMap || {};
          setDataPagosAut(prev => applyBatchDates(prev, fHistMap, fCmpMap));
          setDataAutorizadas(prev => applyBatchDates(prev, fHistMap, fCmpMap));
        }).catch(errHist => console.error('Error fechas batch no bloqueante:', errHist));
      }

      // Si el usuario está viendo la pestaña de Vista General (Tab 2), refrescarla también
      if (activeFullTabRef.current === 2) {
        if (loadProgramadasDataRef.current) loadProgramadasDataRef.current(true);
      } else {
        programadasLoadedRef.current = false;
      }
    } catch (err) {
      console.error('Error cargando ResumenCentroCosto:', err);
      setLoading(false);
    }
  }, [fetchPagosAutData, fetchReporteAutorizadasData, processRows, apiUrl, applyBatchDates]);

  // Ejecución inicial protegida: corre exactamente una vez al montar
  useEffect(() => {
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    fetchAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mantener actualizado activeFullTabRef y cargar Vista General cuando se active Tab 2
  useEffect(() => {
    activeFullTabRef.current = activeFullTab;
    if (activeFullTab === 2 && !programadasLoadedRef.current && !loadingProgramadas) {
      if (loadProgramadasDataRef.current) {
        loadProgramadasDataRef.current();
      }
    }
  }, [activeFullTab, loadingProgramadas]);

  // Re-procesar fechas de reporte cuando llegue el historialMap
  useEffect(() => {
    if (!Object.keys(historialMap).length) return;

    const resolveFechaReportePago = (row) => {
      if (row.FECHA_REPORTE_PAGO) return { formatted: fmtDate(row.FECHA_REPORTE_PAGO), rawDate: row.FECHA_REPORTE_PAGO };
      const keys = getReporteKeys(row);
      for (const k of keys) {
        const hist = historialMap[k];
        const fechaReporteLote = hist?.eventos?.[hist.eventos.length - 1]?.fecha;
        if (fechaReporteLote) return { formatted: fmtDate(fechaReporteLote), rawDate: fechaReporteLote };
      }
      if (row.FECHA_SOLICITUD_PAGO && row.FECHA_SOLICITUD_PAGO !== '🟡 Sin solicitar') return { formatted: fmtDate(row.FECHA_SOLICITUD_PAGO), rawDate: row.FECHA_SOLICITUD_PAGO };
      if (row.FECHA_REPORTE) return { formatted: fmtDate(row.FECHA_REPORTE), rawDate: row.FECHA_REPORTE };
      return { formatted: '', rawDate: null };
    };

    const resolveDiasSinPago = (row, fechaReporteObj) => {
      const estatusId = Number(row.ESTATUSID ?? row.ID_ESTATUS ?? row.ESTATUS_ID ?? row.estatusId ?? 0);
      const estatusText = String(row.ESTATUS || row.ESTATUS_COMPACTO || '').toLowerCase();
      const esPagadaOCancelada = estatusId === 10 || estatusText.includes('pagad') || estatusText.includes('cancelad');
      if (esPagadaOCancelada) return '';
      if (!fechaReporteObj.rawDate || !fechaReporteObj.formatted) return '';
      return `${calcDiasRetraso(fechaReporteObj.rawDate)}d`;
    };

    const getVeces = (row) => {
      const keys = getReporteKeys(row);
      for (const k of keys) {
        if (historialMap[k]?.veces) return historialMap[k].veces;
      }
      return row.VECES_REPORTADO || 0;
    };

    const updateFechaReporte = (rows) => (rows || []).map(r => {
      const fReporte = resolveFechaReportePago(r);
      return {
        ...r,
        FECHA_REPORTE_PAGO: fReporte.formatted,
        DIAS_SIN_PAGO: resolveDiasSinPago(r, fReporte),
        VECES_REPORTADO: getVeces(r),
      };
    });

    setDataPagosAut(prev => updateFechaReporte(prev));
    setDataAutorizadas(prev => updateFechaReporte(prev));
    setDataProgramadas(prev => updateFechaReporte(prev));
  }, [historialMap]);

  // ── Cargar Catálogos Jerárquicos (Empresas, Proyectos, Divisiones, Centros de Costo) ──
  useEffect(() => {
    const cargarEmpresas = async () => {
      if (!apiUrl || !usuarioId) return;
      try {
        const response = await axios.get(`${apiUrl}/api/empresas/${usuarioId}`);
        const empresas = response.data?.empresas || [];
        const mapped = empresas
          .map(empresa => ({
            id: Number(empresa.EMPRESAID),
            nombre: empresa.NOMBRE_EMPRESA,
          }))
          .filter(empresa => empresaIds.includes(empresa.id));
        setEmpresaOptions(mapped);
      } catch (err) {
        console.error('Error cargando empresas para ResumenCentroCosto:', err);
        setEmpresaOptions([]);
      }
    };
    cargarEmpresas();
  }, [apiUrl, usuarioId, empresaIds]);

  const selectedCatalogEmpresaIds = useMemo(() => {
    if (filtroEmpresas.length > 0) return filtroEmpresas;
    return empresaIds;
  }, [filtroEmpresas, empresaIds]);

  const lastLoadedCatalogEmpresasRef = useRef('');

  useEffect(() => {
    if (!apiUrl || !selectedCatalogEmpresaIds.length) return;
    const key = selectedCatalogEmpresaIds.slice().sort().join(',');
    if (lastLoadedCatalogEmpresasRef.current === key) return;
    lastLoadedCatalogEmpresasRef.current = key;

    axios.post(`${apiUrl}/proyectosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setProyectosCatalogo(raw.filter(p => Boolean(p.ACTIVO)));
      })
      .catch(() => setProyectosCatalogo([]));

    axios.post(`${apiUrl}/divisionesTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setDivisionesCatalogo(raw.filter(d => Boolean(d.ACTIVO)));
      })
      .catch(() => setDivisionesCatalogo([]));

    axios.post(`${apiUrl}/centroCostosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setCentrosCostoCatalogo(raw.filter(c => Boolean(c.ACTIVO)));
      })
      .catch(() => setCentrosCostoCatalogo([]));
  }, [apiUrl, selectedCatalogEmpresaIds, usuarioId, idPerfil]);

  const proyectoOptions = useMemo(() => {
    // Proyectos permitidos basados en los centros de costo que realmente tiene el usuario
    const allowedProyIdsFromCc = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_PROYECTO)).filter(id => id > 0)
    );

    // También incluir proyectos que existan en los datos cargados de las tablas
    const allData = [...dataPagosAut, ...dataAutorizadas, ...dataProgramadas];
    allData.forEach(r => {
      const pid = Number(r.ID_PROYECTO || r.IDPROYECTO || 0);
      if (pid > 0) allowedProyIdsFromCc.add(pid);
    });

    const map = new Map();
    proyectosCatalogo.forEach(p => {
      const pid = Number(p.ID_PROYECTO);
      if (allowedProyIdsFromCc.has(pid)) {
        if (p.ID_PROYECTO && p.NOMBRE_PROYECTO) {
          map.set(pid, p.NOMBRE_PROYECTO);
        }
      }
    });

    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

    // Solo agregar "(Sin Proyecto Asignado)" si existen registros sin proyecto asignado
    const hasSinProyecto = allData.some(r => Number(r.ID_PROYECTO || r.IDPROYECTO || 0) <= 0);
    if (hasSinProyecto) {
      list.push({ id: -1, nombre: '(Sin Proyecto Asignado)' });
    }

    return list;
  }, [proyectosCatalogo, centrosCostoCatalogo, dataPagosAut, dataAutorizadas, dataProgramadas]);

  const divisionOptions = useMemo(() => {
    const selectedProyIds = (filtroProyectos || []).map(Number);
    const allowedDivIdsFromCc = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_DIVISION)).filter(id => id > 0)
    );

    const allData = [...dataPagosAut, ...dataAutorizadas, ...dataProgramadas];
    allData.forEach(r => {
      const did = Number(r.ID_DIVISION || r.IDDIVISION || 0);
      if (did > 0) allowedDivIdsFromCc.add(did);
    });

    const map = new Map();
    divisionesCatalogo.forEach(d => {
      const did = Number(d.ID_DIVISION);
      const pid = Number(d.ID_PROYECTO);

      const matchProy = !selectedProyIds.length || selectedProyIds.includes(pid);
      const matchAllowed = allowedDivIdsFromCc.has(did);

      if (matchProy && matchAllowed) {
        if (d.ID_DIVISION && d.NOMBRE_DIVISION) {
          map.set(did, d.NOMBRE_DIVISION);
        }
      }
    });

    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

    const hasSinDivision = allData.some(r => Number(r.ID_DIVISION || r.IDDIVISION || 0) <= 0);
    if (hasSinDivision) {
      list.push({ id: -1, nombre: '(Sin Grupo Asignado)' });
    }

    return list;
  }, [divisionesCatalogo, centrosCostoCatalogo, filtroProyectos, dataPagosAut, dataAutorizadas, dataProgramadas]);

  const hierarchicalSummary = useMemo(() => {
    const parts = [];
    if (filtroEmpresas.length) {
      parts.push(filtroEmpresas.length === 1 ? empresaOptions.find(e => e.id === filtroEmpresas[0])?.nombre || '1 Emp' : `${filtroEmpresas.length} Emp`);
    }
    if (filtroProyectos.length) {
      parts.push(filtroProyectos.length === 1 ? proyectoOptions.find(p => p.id === filtroProyectos[0])?.nombre || '1 Proy' : `${filtroProyectos.length} Proy`);
    }
    if (filtroDivisiones.length) {
      parts.push(filtroDivisiones.length === 1 ? divisionOptions.find(d => d.id === filtroDivisiones[0])?.nombre || '1 Gpo' : `${filtroDivisiones.length} Gpos`);
    }
    if (filtroCc) {
      parts.push(filtroCc);
    }
    if (!parts.length) return 'Empresa / Proyecto / Grupo / CC';
    return parts.join(' › ');
  }, [filtroEmpresas, filtroProyectos, filtroDivisiones, filtroCc, empresaOptions, proyectoOptions, divisionOptions]);

  const hasHierarchicalFilter = useMemo(() => {
    return filtroEmpresas.length > 0 || filtroProyectos.length > 0 || filtroDivisiones.length > 0 || Boolean(filtroCc);
  }, [filtroEmpresas, filtroProyectos, filtroDivisiones, filtroCc]);

  // ── Filtros Locales ──────────────────────────────────────────────────────────
  const optCentrosCosto = useMemo(() => {
    const all = [...dataPagosAut, ...dataAutorizadas, ...dataProgramadas];
    return [...new Set(all.map(r => r.CENTROCOSTO || r.NUMEROCC || '').filter(Boolean))].sort();
  }, [dataPagosAut, dataAutorizadas, dataProgramadas]);

  const optDepartamentos = useMemo(() => {
    const all = [...dataPagosAut, ...dataAutorizadas, ...dataProgramadas];
    return [...new Set(all.map(r => r.DEPARTAMENTO || '').filter(Boolean))].sort();
  }, [dataPagosAut, dataAutorizadas, dataProgramadas]);

  const optProveedores = useMemo(() => {
    const all = [...dataPagosAut, ...dataAutorizadas, ...dataProgramadas];
    return [...new Set(all.map(r => r.proveedor || r.PROVEEDOR || '').filter(Boolean))].sort();
  }, [dataPagosAut, dataAutorizadas, dataProgramadas]);

  const filterFn = useCallback((row) => {
    const rowEmpId = Number(row.empresaId || row.EMPRESAID || row.IDEMPRESA || 0);
    if (filtroEmpresas.length > 0 && rowEmpId > 0 && !filtroEmpresas.includes(rowEmpId)) return false;

    // Jerarquía de Proyectos y Divisiones
    const selectedProyIds = (filtroProyectos || []).map(Number);
    const selectedDivIds = (filtroDivisiones || []).map(Number);

    if (selectedProyIds.length > 0) {
      const rowProyId = Number(row.ID_PROYECTO || row.IDPROYECTO || 0);
      const isSinProyectoSelected = selectedProyIds.includes(-1);
      const matchSinProyecto = isSinProyectoSelected && rowProyId <= 0;
      const matchRowDirect = rowProyId > 0 && selectedProyIds.includes(rowProyId);
      if (!matchRowDirect && !matchSinProyecto) return false;
    }

    if (selectedDivIds.length > 0) {
      const rowDivId = Number(row.ID_DIVISION || row.IDDIVISION || 0);
      const isSinDivisionSelected = selectedDivIds.includes(-1);
      const matchSinDivision = isSinDivisionSelected && rowDivId <= 0;
      const matchRowDirect = rowDivId > 0 && selectedDivIds.includes(rowDivId);
      if (!matchRowDirect && !matchSinDivision) return false;
    }

    const cc = String(row.CENTROCOSTO || row.NUMEROCC || '').trim();
    const depto = String(row.DEPARTAMENTO || '').trim();
    const prov = String(row.proveedor || row.PROVEEDOR || '').trim();
    const orden = String(row.orden || row.FOLIO_OC || row.FOLIO || row.ID || '').toLowerCase().trim();

    if (filtroCc && cc !== filtroCc) return false;
    if (filtroDepto && depto !== filtroDepto) return false;
    if (filtroProveedor && prov !== filtroProveedor) return false;

    if (filtroOrden) {
      const q = filtroOrden.toLowerCase().trim();
      if (!orden.includes(q)) return false;
    }

    // Si el usuario selecciona un Proveedor, se ignoran las fechas para traer TODO el historial de ese proveedor
    if (!filtroProveedor && (fechaInicio || fechaFin)) {
      const rawDate = row.FECHASOLICITUD || row.FECHA_SOLICITUD || row.FECHA_OC || row.FECHA || row.FECHA_SOLICITUD_FORMATO || row.FECHA_SOLICITUD_PAGO;
      if (!rawDate) return false;

      const strDate = String(rawDate).trim();
      let rowDateStr = '';

      // Si viene como DD/MM/YYYY
      const mxMatch = strDate.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (mxMatch) {
        rowDateStr = `${mxMatch[3]}-${mxMatch[2]}-${mxMatch[1]}`;
      } else {
        rowDateStr = strDate.split('T')[0].split(' ')[0];
      }

      if (!rowDateStr) return false;

      if (fechaInicio && rowDateStr < fechaInicio) return false;
      if (fechaFin && rowDateStr > fechaFin) return false;
    }

    return true;
  }, [filtroEmpresas, filtroProyectos, filtroDivisiones, filtroCc, filtroDepto, filtroProveedor, filtroOrden, fechaInicio, fechaFin]);

  const filteredPagosAut = useMemo(() => dataPagosAut.filter(filterFn), [dataPagosAut, filterFn]);
  const filteredAutorizadas = useMemo(() => dataAutorizadas.filter(filterFn), [dataAutorizadas, filterFn]);
  const filteredProgramadas = useMemo(() => dataProgramadas.filter(filterFn), [dataProgramadas, filterFn]);

  // ── Altura Dinámica Acordeón ─────────────────────────────────────────────────
  const openCount = (openSplitSec1 ? 1 : 0) + (openSplitSec2 ? 1 : 0) + (openSplitSec3 ? 1 : 0);

  const heightSplitSec1 = useMemo(() => openCount === 1 ? 660 : 360, [openCount]);
  const heightSplitSec2 = useMemo(() => openCount === 1 ? 660 : 360, [openCount]);
  const heightSplitSec3 = useMemo(() => openCount === 1 ? 660 : 360, [openCount]);

  // ── Acciones ────────────────────────────────────────────────────────────────
  const autorizarRow = useCallback(async (row) => {
    const id = row.ID || row.FOLIO || row.AUTSOLPAGOID;
    const nivel = row.NIVEL_PENDIENTE || 1;
    const emp = row.EMPRESA || nombreEmpresa;
    await axios.get(`${apiUrl}/pagosAutAutorizar/${id}/${nivel}/${loggedInUsername}/${emp}`);
  }, [loggedInUsername, nombreEmpresa]);

  const handleBulkAutorizar = useCallback(async () => {
    if (!selectedRowsAut.length) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedRowsAut.map(autorizarRow));
      setSnackbarMessage(`${selectedRowsAut.length} solicitudes autorizadas con éxito.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await fetchAllData();
    } catch (err) {
      console.error('Error autorizar masivo:', err);
    } finally {
      setActionLoading(false);
    }
  }, [selectedRowsAut, autorizarRow, fetchAllData]);

  const handleBulkRechazar = useCallback(() => {
    if (!selectedRowsAut.length) return;
    setCancelTargetRow(selectedRowsAut[0]);
    setObservacionesCancel('');
    setOpenCancelModal(true);
  }, [selectedRowsAut]);

  // ── Exportación Excel con selector de formato (General vs Dividido por Centro de Costo) ────────────────────────
  const handleExportExcelPagoOriginal = useCallback(async (customRows = null, guardarEnHistorial = true) => {
    const seleccionadas = (Array.isArray(customRows) && customRows.length)
      ? customRows
      : (selectedRowsReporte.length ? selectedRowsReporte : filteredAutorizadas);
    if (!seleccionadas.length) {
      handleOpenSweetAlert('No hay órdenes para exportar en este momento', 'warning');
      return;
    }

    const modalHtml = `
      <div style="font-family: inherit; font-size: 13.5px; text-align: center; color: #475569; padding: 0 4px;">
        <p style="margin: 0 0 16px 0; line-height: 1.45; font-weight: 600; color: #0f172a;">
          Selecciona cómo deseas organizar el archivo Excel para las <strong>${seleccionadas.length}</strong> órdenes:
        </p>
        <div style="display: flex; flex-direction: column; gap: 10px; text-align: left;">
          <label style="display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border: 1.5px solid #cbd5e1; border-radius: 10px; cursor: pointer; background: #f8fafc; transition: all 0.15s;" onmouseover="this.style.borderColor='#0284c7'" onmouseout="this.style.borderColor='#cbd5e1'">
            <input type="radio" name="swal_excel_mode" value="estandar" checked style="margin-top: 3px; accent-color: #0284c7; cursor: pointer;" />
            <div>
              <div style="font-weight: 700; color: #0f172a; font-size: 13px;">📋 Vista General (Todas juntas)</div>
              <div style="font-size: 11px; color: #64748b;">Una sola hoja con todas las órdenes + Hoja con Resumen de Proveedores.</div>
            </div>
          </label>
          <label style="display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border: 1.5px solid #cbd5e1; border-radius: 10px; cursor: pointer; background: #f8fafc; transition: all 0.15s;" onmouseover="this.style.borderColor='#0284c7'" onmouseout="this.style.borderColor='#cbd5e1'">
            <input type="radio" name="swal_excel_mode" value="por_cc" style="margin-top: 3px; accent-color: #0284c7; cursor: pointer;" />
            <div>
              <div style="font-weight: 700; color: #0f172a; font-size: 13px;">📑 Por Grupos / Proyectos (Hojas separadas)</div>
              <div style="font-size: 11px; color: #64748b;">Organiza las órdenes en pestañas según el Grupo/Proyecto al que pertenece su Centro de Costo.</div>
            </div>
          </label>
        </div>
      </div>
    `;

    const result = await handleOpenSweetAlert(modalHtml, 'info', {
      useHtml: true,
      showCancelButton: true,
      confirmButtonText: 'Descargar Excel',
      cancelButtonText: 'Cancelar',
      width: 'clamp(320px, 90vw, 460px)',
      preConfirm: () => {
        const selected = document.querySelector('input[name="swal_excel_mode"]:checked');
        return selected ? selected.value : 'estandar';
      }
    });

    if (!result.isConfirmed) return;
    const modoPorCC = result.value === 'por_cc';

    try {
      setActionLoading(true);
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Sistema de Pagos ESEASA';
      workbook.created = new Date();

      const sanitizeSheetName = (name, fallback) => {
        let clean = String(name || fallback || 'Hoja')
          .replace(/[*?:/\\\[\]]/g, '_')
          .trim();
        if (clean.length > 31) clean = clean.substring(0, 31);
        return clean || fallback || 'Hoja';
      };

      const getGrupoCentroCosto = (row) => {
        // 0. Si ya tiene división o grupo explícito en la fila
        const divFila = String(row.NOMBRE_DIVISION || row.DIVISION || row.GRUPO_CC || row.GRUPO || '').trim();
        if (divFila) return divFila;

        // 1. Buscar en el catálogo de centros de costos por ID o número/nombre para obtener su División
        const ccId = Number(row.ID_CENTRO_COSTO || row.CENTROCOSTOID || row.CENTROCOSTO_ID || 0);
        const ccNum = String(row.NUMEROCC || row.NUMERO_CC || row.centroCosto || row.CENTROCOSTO || '').trim();
        const ccNombre = String(row.NOMBRE_CC || row.centroCostoNombre || row.centroCosto || '').trim();

        const matchCat = centrosCostoCatalogo.find(c => 
          (ccId > 0 && Number(c.ID_CENTRO_COSTO) === ccId) ||
          (ccNum && String(c.NUMERO_CC || c.NUMEROCC || '').trim() === ccNum) ||
          (ccNombre && String(c.NOMBRE_CC || c.NOMBRE_CENTRO_COSTO || '').trim().toLowerCase() === ccNombre.toLowerCase())
        );

        if (matchCat) {
          const divId = Number(matchCat.ID_DIVISION);
          if (divId > 0) {
            const divEncontrada = divisionesCatalogo.find(d => Number(d.ID_DIVISION) === divId);
            if (divEncontrada?.NOMBRE_DIVISION) return divEncontrada.NOMBRE_DIVISION;
          }
          if (matchCat.NOMBRE_DIVISION) return matchCat.NOMBRE_DIVISION;
          if (matchCat.NOMBRE_PROYECTO) return matchCat.NOMBRE_PROYECTO;
        }

        const rawCc = ccNombre || ccNum;
        if (!rawCc) return 'GENERAL';

        const upper = rawCc.toUpperCase();

        // 1. DUCTOS no se agrupa en una sola familia, cada proyecto de DUCTOS mantiene su hoja individual
        if (upper.includes('DUCTO')) {
          return rawCc;
        }

        // 2. Familias Principales Conocidas (para agrupar todas sus variantes en una sola pestaña)
        if (upper.includes('INDIRECTO')) {
          return 'INDIRECTOS';
        }
        if (upper.includes('INFRAESTRUCTURA')) {
          return 'INFRAESTRUCTURA';
        }
        if (upper.includes('ESTRUCTURA LIGERA MARINA') || upper.includes('ESTRUCTURA LIGERA')) {
          return 'ESTRUCTURA LIGERA MARINA';
        }
        if (upper.includes('ESTRUCTURA') || upper.includes('ESTRUCTURAS')) {
          return 'ESTRUCTURAS';
        }
        if (upper.includes('MANTENIMIENTO')) {
          return 'MANTENIMIENTO';
        }
        if (upper.includes('FABRICACION') || upper.includes('FABRICACIÓN')) {
          return 'FABRICACIÓN';
        }
        if (upper.includes('ADMINISTRAC') || upper.includes('ADMINISTRAT')) {
          return 'ADMINISTRACIÓN';
        }
        if (upper.includes('OPERACION') || upper.includes('OPERACIÓN')) {
          return 'OPERACIÓN';
        }
        if (upper.includes('LOGISTICA') || upper.includes('LOGÍSTICA')) {
          return 'LOGÍSTICA';
        }
        if (upper.includes('TRANSPORTE')) {
          return 'TRANSPORTES';
        }

        // 3. Limpieza general para otras variantes (quitar terminaciones de Anexos, Fases, Genéricas, etc.)
        let base = rawCc
          .replace(/\s*\([^)]*\)\s*$/g, '')
          .replace(/\s*[-–—]\s*(ANEXO|FASE|ETAPA|SUB|REV|PARTE|GENERICO|GENÉRICO|GENERICA|GENÉRICA|\d+).*$/i, '')
          .trim();

        return base || rawCc;
      };

      const getRowTimestamp = (r) => {
        const rawDate = r.FECHASOLICITUD || r.FECHA_SOLICITUD_FORMATO || r.FECHA_SOLICITUD || r.FECHA_AUTORIZACION || r.FECHA_OC || r.FECHA;
        if (rawDate) {
          const t = new Date(rawDate).getTime();
          if (!isNaN(t)) return t;
        }
        const numId = Number(r.AUTSOLPAGOID || r.ID_SOLICITUD || r.ID || r.FOLIO || r.orden || 0);
        return numId || 0;
      };

      const renderOrdenesSheet = (sheet, rows) => {
        // Ordenar del más reciente al más antiguo
        const sortedRows = [...rows].sort((a, b) => getRowTimestamp(b) - getRowTimestamp(a));

        // Verificar si alguna orden tiene datos en las columnas opcionales
        const hasEconomico = sortedRows.some(r => String(r.NUMERO_ECONOMICO || r.ECONOMICO || '').trim() !== '');
        const hasExhibit = sortedRows.some(r => String(r.EXHIBIT || r.EXHIBICION || r.exhibit || '').trim() !== '');
        const hasComprobante = sortedRows.some(r => String(r.COMPROBANTE || r.FOLIO_COMPROBANTE || r.REFERENCIA || (r.HAS_COMPROBANTE ? 'SI' : '') || '').trim() !== '');

        const cols = [
          { header: 'ORD', key: 'orden', width: 13 },
          { header: 'EMPRESA', key: 'empresa', width: 22 },
          { header: 'PAGO SOLICITADO', key: 'pago', width: 20 },
          { header: 'MONEDA', key: 'moneda', width: 11 },
          { header: 'CONDICIÓN DE PAGO', key: 'concepto', width: 48 },
          { header: 'DESCRIPCIÓN', key: 'descripcion', width: 48 },
        ];

        if (hasEconomico) {
          cols.push({ header: 'ECONOMICO', key: 'economico', width: 22 });
        }

        cols.push({ header: 'CENTRO DE COSTOS', key: 'centro_costo', width: 34 });
        cols.push({ header: 'PROVEEDOR', key: 'proveedor', width: 40 });

        if (hasExhibit) {
          cols.push({ header: 'EXHIBIT', key: 'exhibit', width: 18 });
        }

        if (hasComprobante) {
          cols.push({ header: 'COMPROBANTE', key: 'comprobante', width: 22 });
        }

        cols.push({ header: 'AUTORIZAR', key: 'autorizar', width: 15 });

        sheet.columns = cols;

        sheet.views = [{ state: 'frozen', ySplit: 1 }];
        const lastColLetter = String.fromCharCode(64 + cols.length);
        sheet.autoFilter = `A1:${lastColLetter}1`;
        sheet.getRow(1).height = 24;
        sheet.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 10 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2F75B5' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          applyThinBorder(cell, 'FFFFFF');
        });

        sortedRows.forEach(row => {
          const descText = Array.isArray(row.partidas) && row.partidas.length
            ? row.partidas.map(p => p.DESCRIPCION_PART || p.DESCRIPCION || '').filter(Boolean).join('\n')
            : (row.descripcion || row.DESCRIPCION || row.DESCRIPCION_OC || row.DESCRIPCION_PART || row.CONCEPTOPAGO || row.CONCEPTO || row.OBSERVACIONES || '');

          const rowData = {
            orden: row.orden || row.FOLIO || row.ID,
            empresa: row.empresa || row.EMPRESA,
            pago: parseMoneyVal(row),
            moneda: row.moneda || 'MXN',
            concepto: row.concepto || row.CONCEPTOPAGO || '',
            descripcion: descText,
            centro_costo: row.centroCosto || row.CENTROCOSTO || '',
            proveedor: row.proveedor || row.PROVEEDOR,
            autorizar: CHECK_MARK,
          };

          if (hasEconomico) {
            rowData.economico = row.NUMERO_ECONOMICO || row.ECONOMICO || '';
          }
          if (hasExhibit) {
            rowData.exhibit = row.EXHIBIT || row.EXHIBICION || row.exhibit || '';
          }
          if (hasComprobante) {
            rowData.comprobante = row.COMPROBANTE || row.FOLIO_COMPROBANTE || row.REFERENCIA || (row.HAS_COMPROBANTE ? 'SI' : '');
          }

          const excelRow = sheet.addRow(rowData);

          const lineCount = Math.max(
            String(row.concepto || '').split('\n').length || 1,
            String(descText || '').split('\n').length || 1
          );
          excelRow.height = Math.max(20, Math.min(90, lineCount * 16));
          excelRow.eachCell((cell, colNumber) => {
            const isLast = colNumber === cols.length;
            cell.font = { size: 9, bold: false };
            cell.alignment = {
              vertical: 'middle',
              horizontal: isLast ? 'center' : 'left',
              wrapText: cell.value && String(cell.value).length > 25,
            };
            applyThinBorder(cell, 'D9E2F3');
          });
          excelRow.getCell('pago').numFmt = '"$"#,##0.00';
          excelRow.getCell('pago').alignment = { horizontal: 'right', vertical: 'middle' };
          excelRow.getCell('autorizar').font = { size: 14, bold: true, color: { argb: '0070C0' } };
          excelRow.getCell('autorizar').alignment = { horizontal: 'center', vertical: 'middle' };
        });
      };

      if (!modoPorCC) {
        // MODO ESTÁNDAR (1 Hoja General)
        const sheet1 = workbook.addWorksheet('Ordenes de compra');
        renderOrdenesSheet(sheet1, seleccionadas);
      } else {
        // MODO DIVIDIDO POR CENTRO DE COSTO (1 Hoja por cada Grupo de CC)
        const porCC = seleccionadas.reduce((acc, row) => {
          const ccGrupo = getGrupoCentroCosto(row);
          if (!acc[ccGrupo]) acc[ccGrupo] = [];
          acc[ccGrupo].push(row);
          return acc;
        }, {});

        const usedSheetNames = new Set();
        Object.keys(porCC).sort().forEach((cc, idx) => {
          let sheetName = sanitizeSheetName(cc, `CC_${idx + 1}`);
          let suffix = 1;
          while (usedSheetNames.has(sheetName.toLowerCase())) {
            sheetName = sanitizeSheetName(`${cc.substring(0, 27)}_${suffix++}`, `CC_${idx + 1}`);
          }
          usedSheetNames.add(sheetName.toLowerCase());

          const sheetCC = workbook.addWorksheet(sheetName);
          renderOrdenesSheet(sheetCC, porCC[cc]);
        });
      }

      // Detección dinámica de todas las monedas presentes en la selección
      const rawCurrencies = Array.from(
        new Set(
          seleccionadas
            .map(r => String(r.moneda || 'MXN').trim().toUpperCase())
            .filter(Boolean)
        )
      );

      const standardOrder = ['MXN', 'USD', 'EUR'];
      const activeCurrencies = [];
      standardOrder.forEach(c => {
        if (rawCurrencies.includes(c)) activeCurrencies.push(c);
      });
      rawCurrencies.forEach(c => {
        if (!activeCurrencies.includes(c)) activeCurrencies.push(c);
      });
      if (activeCurrencies.length === 0) activeCurrencies.push('MXN', 'USD');

      const totalCols = 1 + activeCurrencies.length;
      const getResumenColLetter = (idx) => {
        let letter = '';
        while (idx > 0) {
          const mod = (idx - 1) % 26;
          letter = String.fromCharCode(65 + mod) + letter;
          idx = Math.floor((idx - mod) / 26);
        }
        return letter || 'A';
      };
      const lastResumenCol = getResumenColLetter(totalCols);

      // HOJA RESUMEN
      const sheetResumen = workbook.addWorksheet('Resumen de OC autorizadas');
      sheetResumen.columns = [
        { key: 'proveedor', width: 60 },
        ...activeCurrencies.map(c => ({ key: c.toLowerCase(), width: 18 })),
      ];

      sheetResumen.mergeCells(`A1:${lastResumenCol}1`);
      sheetResumen.getCell('A1').value = 'Resumen de solicitud de pago de ordenes de compra';
      sheetResumen.getCell('A1').font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
      sheetResumen.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
      sheetResumen.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F3F6D' } };
      sheetResumen.getRow(1).height = 28;

      sheetResumen.mergeCells(`A2:${lastResumenCol}2`);
      sheetResumen.getCell('A2').value = modoPorCC ? 'Resumen general (Dividido por C.C.)' : 'O.C. autorizadas';
      sheetResumen.getCell('A2').font = { bold: true, italic: true, size: 13, color: { argb: 'FFFFFF' } };
      sheetResumen.getCell('A2').alignment = { horizontal: 'center', vertical: 'middle' };
      sheetResumen.getCell('A2').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F3F6D' } };
      sheetResumen.getRow(2).height = 24;

      sheetResumen.addRow({});
      const header = sheetResumen.addRow(['Proveedor / Orden', ...activeCurrencies]);
      header.eachCell((cell) => {
        cell.font = { bold: true, size: 12, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '18836E' } };
        cell.alignment = { horizontal: cell.col === 1 ? 'left' : 'center', vertical: 'middle' };
      });

      const grouped = seleccionadas.reduce((acc, row) => {
        const prov = row.proveedor || row.PROVEEDOR || 'SIN PROVEEDOR';
        if (!acc[prov]) acc[prov] = [];
        acc[prov].push(row);
        return acc;
      }, {});

      const totalesPorMoneda = {};
      activeCurrencies.forEach(c => { totalesPorMoneda[c] = 0; });

      Object.keys(grouped).sort().forEach(prov => {
        const providerRows = grouped[prov];
        const subtotales = activeCurrencies.map(c => {
          const sub = providerRows
            .filter(row => String(row.moneda || 'MXN').trim().toUpperCase() === c)
            .reduce((sum, row) => sum + parseMoneyVal(row), 0);
          return sub;
        });

        activeCurrencies.forEach((c, i) => {
          totalesPorMoneda[c] += subtotales[i];
        });

        const provRow = sheetResumen.addRow([
          prov,
          ...subtotales.map(s => (s > 0 ? s : ''))
        ]);
        provRow.eachCell((cell, colNumber) => {
          cell.font = { bold: true, size: 12, color: { argb: '000000' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'B4C7E7' } };
          cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'right', vertical: 'middle' };
          if (colNumber > 1) cell.numFmt = '"$"#,##0.00';
        });

        const sortedProviderRows = [...providerRows].sort((a, b) => getRowTimestamp(b) - getRowTimestamp(a));
        sortedProviderRows.forEach(row => {
          const rMoneda = String(row.moneda || 'MXN').trim().toUpperCase();
          const detailRow = sheetResumen.addRow([
            row.orden || row.FOLIO || row.ID,
            ...activeCurrencies.map(c => (rMoneda === c ? parseMoneyVal(row) : ''))
          ]);
          detailRow.eachCell((cell, colNumber) => {
            cell.font = { size: 11 };
            cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'right', vertical: 'middle' };
            if (colNumber > 1) cell.numFmt = '"$"#,##0.00';
          });
        });
      });

      sheetResumen.addRow({});
      const totalRow = sheetResumen.addRow([
        'TOTAL',
        ...activeCurrencies.map(c => totalesPorMoneda[c] || 0)
      ]);
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true, size: 13, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F3F6D' } };
        cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'right', vertical: 'middle' };
        if (colNumber > 1) cell.numFmt = '"$"#,##0.00';
      });

      const fileName = `OC_Autorizadas_${modoPorCC ? 'PorCC_' : ''}${new Date().getTime()}.xlsx`;

      if (guardarEnHistorial) {
        await axios.post(`${apiUrl}/reporteAutorizadas/guardarHistorial`, {
          usuario: loggedInUsername,
          archivo: fileName,
          items: seleccionadas.map(buildHistorialItem),
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);

      setSnackbarMessage(`Excel generado exitosamente (${seleccionadas.length} órdenes).`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      if (guardarEnHistorial) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Error generando Excel:', err);
      handleOpenSweetAlert('Error generando el archivo Excel.', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [selectedRowsReporte, filteredAutorizadas, loggedInUsername, fetchAllData, apiUrl, centrosCostoCatalogo, divisionesCatalogo]);

  const handleVerArchivos = useCallback(async (row) => {
    try {
      setActionLoading(true);
      const { adjuntos } = await obtenerArchivosPago(row.ID || row.FOLIO);
      if (!adjuntos || !adjuntos.length) {
        handleOpenSweetAlert('No hay archivos adjuntos para esta solicitud.', 'info');
        return;
      }
      const files = adjuntos.map(file => {
        const ext = (file.filename || '').split('.').pop().toLowerCase();
        const mimeType = ext === 'pdf' ? 'application/pdf' : ['jpg','jpeg','png'].includes(ext) ? `image/${ext}` : 'application/octet-stream';
        return {
          id: file.archivo_id,
          name: file.filename,
          url: `${apiUrl}/api/archivos-pago/download/${file.archivo_id}`,
          type: mimeType,
        };
      });
      setFileList(files);
      setInitialFileId(files[0]?.id || null);
      setFileModalOpen(true);
    } catch (err) {
      console.error('Error obteniendo archivos:', err);
      handleOpenSweetAlert('Error cargando archivos adjuntos.', 'error');
    } finally {
      setActionLoading(false);
    }
  }, []);

  const handleConfirmCancelacion = useCallback(async (correctionData) => {
    if (!cancelTargetRow) return;
    setOpenCancelModal(false);
    setActionLoading(true);
    try {
      const payload = {
        solicitudId: cancelTargetRow.ID || cancelTargetRow.FOLIO,
        usuario: loggedInUsername,
        observaciones: observacionesCancel,
        correccionData: correctionData || null,
        empresa: cancelTargetRow.EMPRESA || nombreEmpresa
      };
      await cancelarPago(payload);
      setSnackbarMessage('Solicitud rechazada correctamente.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await fetchAllData();
    } catch (err) {
      console.error('Error al rechazar:', err);
      handleOpenSweetAlert('Error al procesar el rechazo.', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [cancelTargetRow, loggedInUsername, observacionesCancel, nombreEmpresa, fetchAllData]);

  // ── MANTENEMOS TODOS LOS DATOS ORIGINALES SIN DESAPARECER NADA ────────────────
  const formattedDataAut = useMemo(() => filteredPagosAut.map(row => ({
    ...row,
    FECHA_SOLICITUD: row.FECHA_SOLICITUD_FORMATO,
    FECHA_AUTORIZACION_UNIFICADA: row.FECHA_AUTORIZACION_UNIFICADA,
    FECHA_REPORTE_PAGO: row.FECHA_REPORTE_PAGO,
    DIAS_SIN_PAGO: row.DIAS_SIN_PAGO,
    FECHA_COMPROBANTE: row.FECHA_COMPROBANTE,
    EXHIBIT: row.EXHIBIT,
    ESTATUS_COMPACTO: row.ESTATUS_COMPACTO,
    TIPOPAGO: row.TIPOPAGO || row.TIPO_PAGO || row.PAGO || '',
  })), [filteredPagosAut]);

  const formattedDataReporte = useMemo(() => filteredAutorizadas.map(row => {
    const valMonto = parseMoneyVal(row);
    const divisaStr = (row.DIVISA || row.MONEDA || 'MXN').toString().toUpperCase();
    return {
      ...row,
      TOTALOC: valMonto,
      MONTO: valMonto,
      TOTAL: valMonto,
      pago: valMonto,
      MONTO_SOLICITADO: valMonto,
      MONTOSOLICITADO: valMonto,
      MONTO_UNIFICADO: valMonto,
      PAGOSAUT_IMPORTE: fmt(valMonto),
      TOTAL_DIVISA: `${fmt(valMonto)}\n${divisaStr}`,
      FECHA_SOLICITUD: row.FECHA_SOLICITUD_FORMATO,
      FECHA_AUTORIZACION_UNIFICADA: row.FECHA_AUTORIZACION_UNIFICADA,
      FECHA_REPORTE_PAGO: row.FECHA_REPORTE_PAGO,
      DIAS_SIN_PAGO: row.DIAS_SIN_PAGO,
      FECHA_COMPROBANTE: row.FECHA_COMPROBANTE,
      EXHIBIT: row.EXHIBIT,
      ESTATUS_COMPACTO: row.ESTATUS_COMPACTO,
      VECES_REPORTADO: row.VECES_REPORTADO || 0,
      TIPOPAGO: row.TIPOPAGO || row.TIPO_PAGO || row.PAGO || '',
      partidas: row.partidas || [],
      descripcion: row.descripcion || '',
    };
  }), [filteredAutorizadas]);

  const formattedDataProgramadas = useMemo(() => filteredProgramadas.map(row => ({
    ...row,
    FECHA_SOLICITUD: row.FECHA_SOLICITUD_FORMATO,
    FECHA_AUTORIZACION_UNIFICADA: row.FECHA_AUTORIZACION_UNIFICADA,
    FECHA_REPORTE_PAGO: row.FECHA_REPORTE_PAGO,
    DIAS_SIN_PAGO: row.DIAS_SIN_PAGO,
    FECHA_COMPROBANTE: row.FECHA_COMPROBANTE,
    EXHIBIT: row.EXHIBIT,
    ESTATUS_COMPACTO: row.ESTATUS_COMPACTO,
    TIPOPAGO: row.TIPOPAGO || row.TIPO_PAGO || row.PAGO || '',
    partidas: row.partidas || [],
    descripcion: row.descripcion || '',
  })), [filteredProgramadas]);

  // ── COLUMNAS VISIBLES (EXCLUSIVAMENTE LAS 13 SELECCIONADAS POR EL USUARIO PARA TODAS LAS TABLAS) ──────────────
  const columnOrder = useMemo(() => [
    'PAGOSAUT_OC_EMPRESA',
    'PROVEEDOR',
    'PAGOSAUT_CENTRO_DEPTO',
    'TOTAL_MN',
    'TOTAL_USD',
    'TOTAL_EUR',
    'FECHA_SOLICITUD',
    'FECHA_AUTORIZACION_UNIFICADA',
    'FECHA_REPORTE_PAGO',
    'EXHIBIT',
    'DIAS_SIN_PAGO',
    'FECHA_COMPROBANTE',
    'TIPOPAGO',
  ], []);

  const columnLabels = useMemo(() => ({
    PAGOSAUT_OC_EMPRESA: 'ORDEN',
    PROVEEDOR: 'CLIENTE',
    PAGOSAUT_CENTRO_DEPTO: 'CENTROCOST/DPTO',
    TOTAL_MN: 'TOTAL MN',
    TOTAL_USD: 'TOTAL USD',
    TOTAL_EUR: 'TOTAL EUR',
    FECHA_SOLICITUD: 'SOLICITUD',
    FECHA_AUTORIZACION_UNIFICADA: 'AUTORIZACION',
    FECHA_REPORTE_PAGO: 'REPORTE PAGO',
    DIAS_SIN_PAGO: 'S/PAGO',
    FECHA_COMPROBANTE: 'COMPROBANTE',
    EXHIBIT: 'EXHIBIT',
    ESTATUS_COMPACTO: 'ESTATUS',
    TIPOPAGO: 'Tipo de pago',
  }), []);

  // ÚNICAMENTE OCULTAMOS DE LA VISTA INICIAL LAS SECUNDARIAS SIN ELIMINARLAS DEL OBJETO
  const hiddenFields = useMemo(() => [
    'ID', 'ID_SOLICITUD', 'FOLIO', 'FOLIO_OC', 'MONTO', 'RFC',
    'NOMBRE_EMPRESA', 'EMPRESA', 'EMPRESAID', 'CENTROCOSTO', 'CENTROCOSTOID',
    'NUMEROCC', 'NUMERO_CC', 'DEPARTAMENTO', 'DEPARTAMENTOID', 'SOLICITANTE',
    'REQUISITOR', 'METODOPAGO', 'COMPROBANTE', 'IMAGEN', 'REFERENCIA', 'HORA',
    'TESORERO1', 'CANCELADO_ERP', 'HAS_OC', 'HAS_CB', 'HAS_FACTURA',
    'PAGOSAUT_SEGUIMIENTO', 'CONCEPTOPAGO', 'CONCEPTO', 'TOTALOC', 'DIVISA',
    'MONTOSOLICITADO', 'MONTO_SOLICITADO', 'ESTATUS', 'ESTATUSID', 'ID_ESTATUS', 'AUTSOLPAGOID',
    '_FECHASOLICITUD_SORT', 'FECHASOLICITUD', 'FECHA_AUTORIZACION', 'FECHA_SOLICITUD_PAGO',
    'REQUI', 'FECHA_OC', 'TOTAL', 'NUMERO_ECONOMICO', 'FOLIO_FACTURA',
    'PENDIENTE', 'VENCIMIENTO', 'DIAS_VENCIMIENTO', 'DIASRESTANTES', 'DIAS_VENCIMIENTO_RAW',
    'FECHA_PAGO', 'NOTAS_OBSERVACIONES', 'usuarioCancelo', 'USUARIO_CANCELO', 'OBSERV_CANCELADAS', 'FECHA_CANCELACION',
    'cadenaAut', 'ID_FACTURA', 'ID_CB', 'ID_OC',
    'PAGOSAUT_COMPRADOR_FECHA', 'PAGOSAUT_IMPORTE', 'EXHIBICION', 'MONTO_UNIFICADO', 'FECHA_SOLICITUD_FORMATO', 'VECES_REPORTADO', 'TIPO_PAGO', 'PAGO',
    'ESTATUS_COMPACTO', 'estatusId', 'HORASOLICITUD', 'Centro de costo', 'CENTRO_COSTO', 'CENTROCOSTOS', 'NOMBRE_CC', 'orden', 'empresa', 'pago', 'moneda', 'proveedor', 'centroCosto', 'departamento', 'TOTAL_DIVISA'
  ], []);

  const buttonsSection1 = useMemo(() => [
    {
      id: 1, label: 'Autorizar Solicitud', icon: <TaskAltIcon />, buttonColor: '#16a34a', buttonFontColor: '#fff', isVisible: true,
      onClick: async (row) => {
        setActionLoading(true);
        try { await autorizarRow(row); await fetchAllData(); } finally { setActionLoading(false); }
      }
    },
    {
      id: 2, label: 'Rechazar Solicitud', icon: <CancelIcon />, buttonColor: '#dc2626', buttonFontColor: '#fff', isVisible: true,
      onClick: (row) => {
        setCancelTargetRow(row);
        setObservacionesCancel('');
        setOpenCancelModal(true);
      }
    },
    {
      id: 3, label: 'Ver Archivos Adjuntos', icon: <FolderIcon />, buttonColor: '#0284c7', buttonFontColor: '#fff', isVisible: true,
      onClick: (row) => handleVerArchivos(row)
    }
  ], [autorizarRow, fetchAllData, handleVerArchivos]);

  const buttonsSection2 = useMemo(() => [
    {
      id: 3, label: 'Ver Archivos Adjuntos', icon: <FolderIcon />, buttonColor: '#0284c7', buttonFontColor: '#fff', isVisible: true,
      onClick: (row) => handleVerArchivos(row)
    }
  ], [handleVerArchivos]);

  // ── FOOTER ROW FIJO CON TOTALES POR COLUMNA / DIVISA ───────────────────────────
  const createRenderFooterRow = useCallback((sourceRows) => {
    if (!sourceRows || sourceRows.length === 0) return () => null;

    let sumMxn = 0;
    let sumUsd = 0;
    let sumEur = 0;

    sourceRows.forEach((r) => {
      const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
      const val = parseMoneyVal(r);
      if (divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR') {
        sumUsd += val;
      } else if (divisa === 'EUR' || divisa === 'EUROS' || divisa === 'EURO') {
        sumEur += val;
      } else {
        sumMxn += val;
      }
    });

    return ({ orderedKeys, stickyLeftMeta, hasActions, selectable, hasPartidas }) => {

      return (
        <tfoot
          style={{ position: 'sticky', bottom: 0, zIndex: 12, backgroundColor: '#f8fafc' }}
          onClick={(e) => e.stopPropagation()}
        >
          <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', boxShadow: '0 -4px 10px rgba(15, 23, 42, 0.04)' }}>
            {selectable && <td style={{ backgroundColor: '#f8fafc', padding: '4px 6px' }} />}
            {hasPartidas && <td style={{ backgroundColor: '#f8fafc', padding: '4px 6px' }} />}

            {orderedKeys.map((key, idx) => {
              const isSticky = stickyLeftMeta && stickyLeftMeta[key];
              const stickyStyle = isSticky ? {
                position: 'sticky',
                left: isSticky.left,
                zIndex: 13,
                backgroundColor: '#f8fafc',
                boxShadow: isSticky.isLastSticky ? '3px 0 6px -2px rgba(0, 0, 0, 0.12)' : 'none',
              } : {};

              const isFirstCol = idx === 0;
              let content = null;

              if (isFirstCol) {
                content = (
                  <Typography sx={{ fontWeight: 800, fontSize: '0.68rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    TOTALES ({sourceRows.length})
                  </Typography>
                );
              } else if (key === 'TOTAL_MN') {
                content = (
                  <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: sumMxn > 0 ? '#0f172a' : '#94a3b8', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {sumMxn > 0 ? fmt(sumMxn) : '-'}
                  </Typography>
                );
              } else if (key === 'TOTAL_USD') {
                content = (
                  <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: sumUsd > 0 ? '#0284c7' : '#94a3b8', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {sumUsd > 0 ? `$${sumUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </Typography>
                );
              } else if (key === 'TOTAL_EUR') {
                content = (
                  <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: sumEur > 0 ? '#7c3aed' : '#94a3b8', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {sumEur > 0 ? `€${sumEur.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </Typography>
                );
              }

              return (
                <td
                  key={key}
                  style={{
                    ...stickyStyle,
                    padding: '4px 8px',
                    backgroundColor: '#f8fafc',
                    verticalAlign: 'middle',
                  }}
                >
                  {content}
                </td>
              );
            })}

            {hasActions && <td style={{ backgroundColor: '#f8fafc', padding: '4px 6px' }} />}
          </tr>
        </tfoot>
      );
    };
  }, []);

  const globalPendientesTotals = useMemo(() => {
    let totalMxnGlobal = 0;
    let totalUsdGlobal = 0;
    let totalEurGlobal = 0;

    const sourceList = filtroProveedor
      ? dataAutorizadas.filter(r => String(r.proveedor || r.PROVEEDOR || '').trim() === filtroProveedor)
      : dataAutorizadas;

    sourceList.forEach((r) => {
      const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
      const val = parseMoneyVal(r);
      if (divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR') {
        totalUsdGlobal += val;
      } else if (divisa === 'EUR' || divisa === 'EUROS' || divisa === 'EURO') {
        totalEurGlobal += val;
      } else {
        totalMxnGlobal += val;
      }
    });

    return { totalMxnGlobal, totalUsdGlobal, totalEurGlobal };
  }, [dataAutorizadas, filtroProveedor]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: '#f8fafc', p: 'clamp(6px, 0.8vw, 12px)', gap: 'clamp(6px, 0.8vw, 10px)' }}>

      {/* ── PANEL UNIFICADO: PESTAÑAS + FILTROS EN UN SOLO CONTENEDOR ────────────────── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          px: 'clamp(8px, 1vw, 14px)',
          py: 'clamp(4px, 0.5vw, 6px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(6px, 0.7vw, 9px)',
          flexShrink: 0,
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
        }}
      >
        {/* 1. PESTAÑAS DE NAVEGACIÓN SUPERIORES */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', pb: 0.3 }}>
          <Tabs
            value={activeFullTab}
            onChange={(e, val) => setActiveFullTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 'clamp(28px, 2.2vw, 32px)',
              '& .MuiTab-root': {
                minHeight: 'clamp(28px, 2.2vw, 32px)',
                py: 0,
                px: 'clamp(8px, 1vw, 14px)',
                fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.78rem)',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '7px',
                '&.Mui-selected': {
                  color: '#0655A5',
                }
              }
            }}
          >
            <Tab
              icon={<FactCheckIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 17px) !important' }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <span>Por Autorizar</span>
                  <Box sx={{ bgcolor: activeFullTab === 0 ? 'rgba(6, 85, 165, 0.12)' : '#f1f5f9', color: activeFullTab === 0 ? '#0655A5' : '#64748b', px: 0.7, py: 0.1, borderRadius: '10px', fontSize: '0.66rem', fontWeight: 800 }}>
                    {filteredPagosAut.length}
                  </Box>
                </Box>
              }
            />
            <Tab
              icon={<HourglassTopIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 17px) !important' }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <span>Pendientes por Pagar</span>
                  <Box sx={{ bgcolor: activeFullTab === 1 ? 'rgba(6, 85, 165, 0.12)' : '#f1f5f9', color: activeFullTab === 1 ? '#0655A5' : '#64748b', px: 0.7, py: 0.1, borderRadius: '10px', fontSize: '0.66rem', fontWeight: 800 }}>
                    {filteredAutorizadas.length}
                  </Box>
                </Box>
              }
            />
            <Tab
              icon={<HistoryEduIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 17px) !important' }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <span>Vista General</span>
                  <Box sx={{ bgcolor: activeFullTab === 2 ? 'rgba(6, 85, 165, 0.12)' : '#f1f5f9', color: activeFullTab === 2 ? '#0655A5' : '#64748b', px: 0.7, py: 0.1, borderRadius: '10px', fontSize: '0.66rem', fontWeight: 800 }}>
                    {loadingProgramadas ? '...' : (programadasLoadedRef.current ? filteredProgramadas.length : '—')}
                  </Box>
                </Box>
              }
            />
          </Tabs>

          <Typography sx={{ fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.74rem)', color: '#64748b', fontWeight: 700, pr: 0.5 }}>
            {activeFullTab === 0 ? `${filteredPagosAut.length} por autorizar` : activeFullTab === 1 ? `${filteredAutorizadas.length} listas para pago` : (loadingProgramadas ? 'Cargando solicitudes...' : `${filteredProgramadas.length} en total`)}
          </Typography>
        </Box>

        {/* 2. BARRA DE FILTROS INFERIOR */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'clamp(6px, 0.8vw, 12px)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 0.5vw, 8px)', flexWrap: 'wrap' }}>
            
            {/* Rango de Fechas */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                type="date"
                size="small"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                sx={{
                  width: 'clamp(110px, 10vw, 128px)',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '7px',
                    bgcolor: '#f8fafc',
                    fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem)',
                    fontWeight: 600,
                    height: 'clamp(26px, 2.2vw, 32px)',
                  }
                }}
              />
              <Typography sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.68rem)', color: '#94a3b8', fontWeight: 700 }}>a</Typography>
              <TextField
                type="date"
                size="small"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                sx={{
                  width: 'clamp(110px, 10vw, 128px)',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '7px',
                    bgcolor: '#f8fafc',
                    fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem)',
                    fontWeight: 600,
                    height: 'clamp(26px, 2.2vw, 32px)',
                  }
                }}
              />
            </Box>

            {/* ── MODO COMPACTO (PANTALLAS MEDIANAS/CHICAS: xs..xl) ── */}
            <Box sx={{ display: { xs: 'flex', xl: 'none' }, alignItems: 'center', gap: 'clamp(4px, 0.5vw, 8px)', flexWrap: 'wrap' }}>
              {/* Botón Popover: Jerarquía Completa (Empresa > Proyecto > Grupo > CC) */}
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => setHierarchicalAnchorEl(e.currentTarget)}
                startIcon={<AccountTreeIcon sx={{ color: hasHierarchicalFilter ? '#0655A5' : '#64748b', fontSize: 16 }} />}
                endIcon={<ExpandMoreIcon sx={{ fontSize: 14, color: '#64748b' }} />}
                sx={{
                  height: 'clamp(26px, 2.2vw, 32px)',
                  px: 1.2,
                  borderRadius: '7px',
                  textTransform: 'none',
                  borderColor: hasHierarchicalFilter ? '#60a5fa' : '#cbd5e1',
                  bgcolor: hasHierarchicalFilter ? '#f0f7ff' : '#ffffff',
                  color: hasHierarchicalFilter ? '#0655A5' : '#475569',
                  fontWeight: hasHierarchicalFilter ? 700 : 500,
                  fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem)',
                  maxWidth: 240,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  flexShrink: 0,
                  '&:hover': {
                    borderColor: '#0655A5',
                    bgcolor: hasHierarchicalFilter ? '#e0f2fe' : '#f8fafc',
                  }
                }}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {hierarchicalSummary}
                </Box>
              </Button>

              {/* Departamento */}
              <Autocomplete
                size="small"
                options={optDepartamentos}
                value={filtroDepto || null}
                onChange={(event, newValue) => setFiltroDepto(newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Departamento"
                    sx={{
                      width: 'clamp(125px, 12vw, 170px)',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '7px',
                        bgcolor: '#f8fafc',
                        fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
                        fontWeight: 600,
                        height: 'clamp(26px, 2.2vw, 32px)',
                      }
                    }}
                  />
                )}
              />

              {/* Cliente / Proveedor */}
              <Autocomplete
                size="small"
                options={optProveedores}
                value={filtroProveedor || null}
                onChange={(event, newValue) => setFiltroProveedor(newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Cliente"
                    sx={{
                      width: 'clamp(135px, 14vw, 195px)',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '7px',
                        bgcolor: '#f8fafc',
                        fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
                        fontWeight: 600,
                        height: 'clamp(26px, 2.2vw, 32px)',
                      }
                    }}
                  />
                )}
              />
            </Box>

            {/* ── MODO COMPLETO ESCRITORIO (PANTALLAS GRANDES: xl+) ── */}
            <Box sx={{ display: { xs: 'none', xl: 'flex' }, alignItems: 'center', gap: 'clamp(4px, 0.5vw, 6px)', flexWrap: 'nowrap' }}>
              {/* Empresa */}
              <Autocomplete
                disabled={loading}
                multiple
                disableCloseOnSelect
                forcePopupIcon
                size="small"
                options={empresaOptions}
                value={empresaOptions.filter(empresa => filtroEmpresas.includes(empresa.id))}
                onChange={(_, values) => {
                  setFiltroEmpresas((values || []).map(v => v.id));
                  setFiltroProyectos([]);
                  setFiltroDivisiones([]);
                  setFiltroCc('');
                }}
                getOptionLabel={(option) => option?.nombre || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                limitTags={1}
                renderTags={() => null}
                renderOption={renderCheckboxOption}
                renderInput={(params) => renderSummaryInput(
                  params,
                  'Empresa',
                  empresaOptions.filter(empresa => filtroEmpresas.includes(empresa.id))
                )}
                sx={{ width: 'clamp(105px, 8.5vw, 130px)', flexShrink: 0, ...compactAutocompleteSx }}
              />

              {/* Proyecto */}
              <Autocomplete
                disabled={loading}
                multiple
                disableCloseOnSelect
                forcePopupIcon
                size="small"
                options={proyectoOptions}
                value={proyectoOptions.filter(p => filtroProyectos.includes(p.id))}
                isOptionEqualToValue={(option, val) => option?.id === val?.id}
                onChange={(_, values) => {
                  setFiltroProyectos((values || []).map(p => p.id));
                  setFiltroDivisiones([]);
                  setFiltroCc('');
                }}
                limitTags={1}
                renderTags={() => null}
                getOptionLabel={(option) => option?.nombre || ''}
                renderOption={renderCheckboxOption}
                renderInput={(params) => renderSummaryInput(
                  params,
                  'Proyecto',
                  proyectoOptions.filter(p => filtroProyectos.includes(p.id))
                )}
                sx={{ width: 'clamp(95px, 8vw, 120px)', flexShrink: 0, ...compactAutocompleteSx }}
              />

              {/* Grupo / División */}
              <Autocomplete
                disabled={loading}
                multiple
                disableCloseOnSelect
                forcePopupIcon
                size="small"
                options={divisionOptions}
                value={divisionOptions.filter(d => filtroDivisiones.includes(d.id))}
                isOptionEqualToValue={(option, val) => option?.id === val?.id}
                onChange={(_, values) => {
                  setFiltroDivisiones((values || []).map(d => d.id));
                  setFiltroCc('');
                }}
                limitTags={1}
                renderTags={() => null}
                getOptionLabel={(option) => option?.nombre || ''}
                renderOption={renderCheckboxOption}
                renderInput={(params) => renderSummaryInput(
                  params,
                  'Grupo',
                  divisionOptions.filter(d => filtroDivisiones.includes(d.id))
                )}
                sx={{ width: 'clamp(95px, 8vw, 115px)', flexShrink: 0, ...compactAutocompleteSx }}
              />

              {/* Centro de Costo */}
              <Autocomplete
                disabled={loading}
                size="small"
                options={optCentrosCosto}
                value={filtroCc || null}
                onChange={(_, newValue) => setFiltroCc(newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Centro costo"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: filtroCc ? (
                        <Box
                          component="span"
                          sx={{
                            maxWidth: 'calc(100% - 24px)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.68rem)',
                            fontWeight: 700,
                            color: '#0655A5',
                          }}
                        >
                          {filtroCc}
                        </Box>
                      ) : params.InputProps.startAdornment,
                    }}
                    sx={{
                      ...(filtroCc ? {
                        '& .MuiInputBase-root': {
                          bgcolor: '#f0f7ff !important',
                          borderColor: '#93c5fd !important',
                          '& fieldset': {
                            borderColor: '#60a5fa !important',
                            borderWidth: '1.5px !important',
                          }
                        }
                      } : {})
                    }}
                  />
                )}
                sx={{ width: 'clamp(105px, 9vw, 130px)', flexShrink: 0, ...compactAutocompleteSx }}
              />

              {/* Departamento */}
              <Autocomplete
                size="small"
                options={optDepartamentos}
                value={filtroDepto || null}
                onChange={(_, newValue) => setFiltroDepto(newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Departamento"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: filtroDepto ? (
                        <Box
                          component="span"
                          sx={{
                            maxWidth: 'calc(100% - 24px)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.68rem)',
                            fontWeight: 700,
                            color: '#0655A5',
                          }}
                        >
                          {filtroDepto}
                        </Box>
                      ) : params.InputProps.startAdornment,
                    }}
                    sx={{
                      ...(filtroDepto ? {
                        '& .MuiInputBase-root': {
                          bgcolor: '#f0f7ff !important',
                          borderColor: '#93c5fd !important',
                          '& fieldset': {
                            borderColor: '#60a5fa !important',
                            borderWidth: '1.5px !important',
                          }
                        }
                      } : {})
                    }}
                  />
                )}
                sx={{ width: 'clamp(105px, 9vw, 130px)', flexShrink: 0, ...compactAutocompleteSx }}
              />

              {/* Cliente / Proveedor */}
              <Autocomplete
                size="small"
                options={optProveedores}
                value={filtroProveedor || null}
                onChange={(_, newValue) => setFiltroProveedor(newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Proveedor"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: filtroProveedor ? (
                        <Box
                          component="span"
                          sx={{
                            maxWidth: 'calc(100% - 24px)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.68rem)',
                            fontWeight: 700,
                            color: '#0655A5',
                          }}
                        >
                          {filtroProveedor}
                        </Box>
                      ) : params.InputProps.startAdornment,
                    }}
                    sx={{
                      ...(filtroProveedor ? {
                        '& .MuiInputBase-root': {
                          bgcolor: '#f0f7ff !important',
                          borderColor: '#93c5fd !important',
                          '& fieldset': {
                            borderColor: '#60a5fa !important',
                            borderWidth: '1.5px !important',
                          }
                        }
                      } : {})
                    }}
                  />
                )}
                sx={{ width: 'clamp(110px, 9.5vw, 135px)', flexShrink: 0, ...compactAutocompleteSx }}
              />
            </Box>

            {/* Buscar O.C. / Folio */}
            <TextField
              size="small"
              placeholder="Buscar O.C. / Folio..."
              value={filtroOrden}
              onChange={(e) => setFiltroOrden(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b', fontSize: 'clamp(14px, 1.1vw, 17px)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 'clamp(130px, 12vw, 170px)',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '7px',
                  bgcolor: '#f8fafc',
                  fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
                  fontWeight: 600,
                  height: 'clamp(26px, 2.2vw, 32px)',
                }
              }}
            />

            {/* Botones Buscar y Limpiar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<SearchIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => fetchAllData()}
                disabled={loading}
                sx={{
                  borderRadius: '7px',
                  px: 'clamp(10px, 1vw, 14px)',
                  py: 0.2,
                  height: 'clamp(26px, 2.2vw, 32px)',
                  fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem)',
                  fontWeight: 700,
                  textTransform: 'none',
                  bgcolor: '#0655A5',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#054785', boxShadow: 'none' }
                }}
              >
                Buscar
              </Button>

              {(hasHierarchicalFilter || filtroDepto || filtroProveedor || filtroOrden || fechaInicio !== getFirstDayOfYear() || fechaFin !== getTodayDate()) && (
                <Button
                  size="small"
                  startIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                  onClick={() => {
                    setFiltroEmpresas([]);
                    setFiltroProyectos([]);
                    setFiltroDivisiones([]);
                    setFiltroCc('');
                    setFiltroDepto('');
                    setFiltroProveedor('');
                    setFiltroOrden('');
                    setFechaInicio(getFirstDayOfYear());
                    setFechaFin(getTodayDate());
                  }}
                  sx={{
                    borderRadius: '7px',
                    px: 'clamp(8px, 0.8vw, 12px)',
                    py: 0.2,
                    height: 'clamp(26px, 2.2vw, 32px)',
                    fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem)',
                    fontWeight: 700,
                    textTransform: 'none',
                    color: '#dc2626',
                    bgcolor: '#fef2f2',
                    border: '1px solid #fecaca',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: '#fee2e2',
                      borderColor: '#fca5a5',
                      color: '#b91c1c'
                    }
                  }}
                >
                  Limpiar
                </Button>
              )}
            </Box>
          </Box>

          {/* Botón Refrescar */}
          <Tooltip title="Actualizar Datos">
            <IconButton onClick={() => fetchAllData()} disabled={loading} size="small" sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '7px', height: 'clamp(26px, 2.2vw, 32px)', width: 'clamp(26px, 2.2vw, 32px)' }}>
              <RefreshIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 18px)', color: '#475569' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* ── CONTADOR DE SOLICITUDES Y TOTALES GLOBALES (MN Y USD) ─────────────────────────── */}
      <Box sx={{ px: 'clamp(4px, 0.6vw, 8px)', pt: 0.2, pb: 'clamp(4px, 0.6vw, 8px)', bgcolor: '#f8fafc', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.8 }}>
        {/* Totales Generales MN y USD de Pendientes por Pagar (Únicamente en pestaña Pendientes por Pagar o Vista Dividida) */}
        {(viewMode === 'SPLIT' || activeFullTab === 1) ? (
          (() => {
            const { totalMxnGlobal, totalUsdGlobal, totalEurGlobal } = globalPendientesTotals;

            return (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 'clamp(6px, 0.8vw, 10px)', alignItems: 'center', flexWrap: 'wrap', bgcolor: '#ffffff', py: 'clamp(4px, 0.5vw, 6px)', px: 'clamp(8px, 1vw, 14px)', borderRadius: '8px', border: '1px solid #cbd5e1', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                  <Typography sx={{ fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.78rem)', fontWeight: 800, color: '#1e293b' }}>
                    {filtroProveedor ? `🏢 Acumulado Cliente: ${filtroProveedor}` : '📊 Total General Pendientes por Pagar:'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 'clamp(6px, 0.7vw, 9px)', py: 0.2, borderRadius: '5px', bgcolor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                    <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 700, color: '#475569' }}>Pesos (MXN):</Typography>
                    <Typography sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.80rem)', fontWeight: 900, color: '#0f172a' }}>{fmt(totalMxnGlobal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 'clamp(6px, 0.7vw, 9px)', py: 0.2, borderRadius: '5px', bgcolor: '#e0f2fe', border: '1px solid #bae6fd' }}>
                    <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 700, color: '#0369a1' }}>Dólares (USD):</Typography>
                    <Typography sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.80rem)', fontWeight: 900, color: '#0284c7' }}>
                      ${totalUsdGlobal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  {totalEurGlobal > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 'clamp(6px, 0.7vw, 9px)', py: 0.2, borderRadius: '5px', bgcolor: '#f3e8ff', border: '1px solid #d8b4fe' }}>
                      <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 700, color: '#6d28d9' }}>Euros (EUR):</Typography>
                      <Typography sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.80rem)', fontWeight: 900, color: '#7c3aed' }}>
                        €{totalEurGlobal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Botón Descargar Reporte General de Todas las Pendientes (Sin afectar historial) */}
                <Tooltip title="Descarga el reporte de todas las órdenes filtradas sin registrar en historial ni contabilizar lotes">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: '15px !important' }} />}
                    onClick={() => handleExportExcelPagoOriginal(filteredAutorizadas, false)}
                    sx={{
                      fontWeight: 800,
                      borderRadius: '8px',
                      textTransform: 'none',
                      bgcolor: '#0f766e',
                      color: '#ffffff',
                      fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.74rem)',
                      px: 'clamp(10px, 1vw, 14px)',
                      py: 0.5,
                      boxShadow: '0 2px 4px rgba(15, 118, 110, 0.2)',
                      '&:hover': { bgcolor: '#115e59' }
                    }}
                  >
                    Descargar Reporte ({filteredAutorizadas.length})
                  </Button>
                </Tooltip>
              </Box>
            );
          })()
        ) : <Box />}
      </Box>

      {/* ── TABLA CON LAS COLUMNAS UNIFICADAS (Transición instantánea de pestañas sin re-montar) ─────────────────────── */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <Paper elevation={0} sx={{ height: '100%', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: activeFullTab === 0 ? 'flex' : 'none', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
            {activeFullTab === 0 && (
              <CollapsibleTable
                key="full_sec1"
                loading={loading}
                dataTable={formattedDataAut}
                buttons={buttonsSection1}
                mobileCard={{ statusMode: 'none' }}
                hiddenFields={hiddenFields}
                initialColumnOrder={columnOrder}
                columnLabels={columnLabels}
                stickyLeftKeys={[]}
                compactActions
                maxInlineActions={3}
                persistEnabled
                persistKey="ResumenCentroCosto_v140"
                persistVista="layout_perfect1_v140"
                usuarioId={usuarioId}
                empresaId={empresaIds[0] || 0}
                apiUrl={apiUrl}
                selectable={true}
                selectedRows={selectedRowsAut}
                onSelectionChange={(selectedArray) => setSelectedRowsAut(selectedArray)}
                renderFooterRow={createRenderFooterRow(formattedDataAut)}
                selectionActions={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button variant="contained" color="error" size="small" onClick={handleBulkRechazar} sx={{ fontWeight: 'bold', borderRadius: 2, textTransform: 'none' }}>
                      ✕ Rechazar Seleccionadas ({selectedRowsAut.length})
                    </Button>
                    <Button variant="contained" color="primary" size="small" onClick={handleBulkAutorizar} sx={{ fontWeight: 'bold', borderRadius: 2, textTransform: 'none', bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
                      ✓ Autorizar Seleccionadas ({selectedRowsAut.length})
                    </Button>
                  </Box>
                }
              />
            )}
          </Box>

          <Box sx={{ display: activeFullTab === 1 ? 'flex' : 'none', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
            {activeFullTab === 1 && (
              <CollapsibleTable
                key="full_sec2"
                loading={loading}
                dataTable={formattedDataReporte}
                buttons={buttonsSection2}
                mobileCard={{ statusMode: 'none' }}
                hiddenFields={hiddenFields}
                initialColumnOrder={columnOrder}
                columnLabels={columnLabels}
                stickyLeftKeys={[]}
                compactActions
                maxInlineActions={3}
                persistEnabled
                persistKey="ResumenCentroCosto_v180"
                persistVista="layout_perfect2_v180"
                usuarioId={usuarioId}
                empresaId={empresaIds[0] || 0}
                apiUrl={apiUrl}
                selectable={true}
                selectedRows={selectedRowsReporte}
                onSelectionChange={(selectedArray) => setSelectedRowsReporte(selectedArray)}
                renderFooterRow={createRenderFooterRow(formattedDataReporte)}
                selectionActions={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExportExcelPagoOriginal(selectedRowsReporte, true)}
                      sx={{
                        fontWeight: 800,
                        borderRadius: '8px',
                        textTransform: 'none',
                        bgcolor: '#0284c7',
                        fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.78rem)',
                        px: 2,
                        py: 0.5,
                        boxShadow: '0 2px 6px rgba(2, 132, 199, 0.3)',
                        '&:hover': { bgcolor: '#0369a1' }
                      }}
                    >
                      Descargar Reporte ({selectedRowsReporte.length || filteredAutorizadas.length})
                    </Button>

                    {(() => {
                      const seleccionadas = selectedRowsReporte.length ? selectedRowsReporte : filteredAutorizadas;
                      let mxn = 0;
                      let usd = 0;
                      let eur = 0;
                      seleccionadas.forEach((r) => {
                        const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
                        const val = parseMoneyVal(r);
                        if (divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR') {
                          usd += val;
                        } else if (divisa === 'EUR' || divisa === 'EUROS' || divisa === 'EURO') {
                          eur += val;
                        } else {
                          mxn += val;
                        }
                      });

                      return (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: '#f8fafc', py: 0.4, px: 1.2, borderRadius: '7px', border: '1px solid #cbd5e1' }}>
                          <Typography sx={{ fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)', fontWeight: 800, color: '#334155' }}>Selección:</Typography>
                          <Typography sx={{ fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.76rem)', fontWeight: 900, color: '#0f172a' }}>{fmt(mxn)}</Typography>
                          {usd > 0 && (
                            <Typography sx={{ fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.76rem)', fontWeight: 900, color: '#0284c7' }}>
                              | ${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                            </Typography>
                          )}
                          {eur > 0 && (
                            <Typography sx={{ fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.76rem)', fontWeight: 900, color: '#7c3aed' }}>
                              | €{eur.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                }
              />
            )}
          </Box>

          <Box sx={{ display: activeFullTab === 2 ? 'flex' : 'none', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
            {activeFullTab === 2 && (
              <CollapsibleTable
                key="full_sec3"
                loading={loading || loadingProgramadas}
                dataTable={formattedDataProgramadas}
                buttons={buttonsSection2}
                mobileCard={{ statusMode: 'none' }}
                hiddenFields={hiddenFields}
                initialColumnOrder={columnOrder}
                columnLabels={columnLabels}
                stickyLeftKeys={[]}
                compactActions
                maxInlineActions={3}
                persistEnabled
                persistKey="ResumenCentroCosto_v140"
                persistVista="layout_perfect3_v140"
                usuarioId={usuarioId}
                empresaId={empresaIds[0] || 0}
                apiUrl={apiUrl}
                selectable={false}
                renderFooterRow={createRenderFooterRow(formattedDataProgramadas)}
              />
            )}
          </Box>
        </Paper>
      </Box>

      {/* ── Popover: Jerarquía Completa (Empresa > Proyecto > Grupo/División > CC) ── */}
      <Popover
        open={Boolean(hierarchicalAnchorEl)}
        anchorEl={hierarchicalAnchorEl}
        onClose={() => setHierarchicalAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 10,
          sx: {
            mt: 0.8,
            p: 2,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            width: 320,
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountTreeIcon sx={{ color: '#0655A5', fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              Filtro Jerárquico
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHierarchicalAnchorEl(null)}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* 1. Empresa */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            1. EMPRESA
          </Typography>
          <Autocomplete
            disabled={loading}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={empresaOptions}
            value={empresaOptions.filter(empresa => filtroEmpresas.includes(empresa.id))}
            onChange={(_, value) => {
              setFiltroEmpresas((value || []).map(empresa => empresa.id));
              setFiltroProyectos([]);
              setFiltroDivisiones([]);
              setFiltroCc('');
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: '4px 8px', fontSize: 12 }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 6, padding: 0 }}
                  checked={selected}
                />
                {option.nombre}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={filtroEmpresas.length ? undefined : 'Todas las empresas'}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: 12,
                    bgcolor: '#f8fafc',
                  }
                }}
              />
            )}
            fullWidth
          />
        </Box>

        {/* 2. Proyecto */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            2. PROYECTO
          </Typography>
          <Autocomplete
            disabled={loading}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={proyectoOptions}
            value={proyectoOptions.filter(p => filtroProyectos.includes(p.id))}
            onChange={(_, value) => {
              setFiltroProyectos((value || []).map(p => p.id));
              setFiltroDivisiones([]);
              setFiltroCc('');
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: '4px 8px', fontSize: 12 }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 6, padding: 0 }}
                  checked={selected}
                />
                {option.nombre}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={filtroProyectos.length ? undefined : 'Todos los proyectos'}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: 12,
                    bgcolor: '#f8fafc',
                  }
                }}
              />
            )}
            fullWidth
          />
        </Box>

        {/* 3. División / Grupo */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            3. GRUPO / DIVISIÓN
          </Typography>
          <Autocomplete
            disabled={loading}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={divisionOptions}
            value={divisionOptions.filter(d => filtroDivisiones.includes(d.id))}
            onChange={(_, value) => {
              setFiltroDivisiones((value || []).map(d => d.id));
              setFiltroCc('');
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: '4px 8px', fontSize: 12 }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 6, padding: 0 }}
                  checked={selected}
                />
                {option.nombre}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={filtroDivisiones.length ? undefined : 'Todos los grupos'}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: 12,
                    bgcolor: '#f8fafc',
                  }
                }}
              />
            )}
            fullWidth
          />
        </Box>

        {/* 4. Centro de Costo */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            4. CENTRO DE COSTO
          </Typography>
          <Autocomplete
            disabled={loading}
            size="small"
            options={optCentrosCosto}
            value={filtroCc || null}
            onChange={(_, newValue) => setFiltroCc(newValue || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Todos los centros de costo"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: 12,
                    bgcolor: '#f8fafc',
                  }
                }}
              />
            )}
            fullWidth
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => {
              setFiltroEmpresas([]);
              setFiltroProyectos([]);
              setFiltroDivisiones([]);
              setFiltroCc('');
            }}
            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: '#64748b' }}
          >
            Limpiar filtros
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => setHierarchicalAnchorEl(null)}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 700,
              bgcolor: '#0655A5',
              borderRadius: '7px',
              px: 2,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#054785', boxShadow: 'none' }
            }}
          >
            Aplicar
          </Button>
        </Box>
      </Popover>

      {/* ── Modales ────────────────────────────────────────────────────────── */}
      <CancelacionModal
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        onSubmit={handleConfirmCancelacion}
        onObservacionesChange={(e) => setObservacionesCancel(e.target.value)}
        observaciones={observacionesCancel}
      />

      <FileActionModal
        open={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        files={fileList}
        initialFileId={initialFileId}
      />

      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />

      <SimpleSuccessSpinner open={actionLoading} handleClose={() => setActionLoading(false)} message="Procesando..." />
    </Box>
  );
}
